/**
 * Admin API Service
 * All tenant admin API calls
 */

import api from './api.service';

/**
 * Optional per-call override threaded into a handful of methods that are
 * used BOTH as a page's primary content AND as background/dropdown
 * population elsewhere (e.g. getAgents is the Agents page's own list, but
 * also just a name-lookup in ChatLogs.tsx) — so the flag can't be baked into
 * the method itself, it has to be an explicit choice at each call site.
 * `suppressPermissionLock: true` stops api.service.ts's interceptor from
 * auto-showing the global PermissionLockDialog for THIS call's 403 (see
 * permission-lock-detection.util.ts) — use it only where a permission denial
 * should degrade quietly (the call's result silently falls back to empty/
 * defaults) rather than interrupt the user.
 */
/** One team-member note on a contact (Conversation.notes). */
/** GET /admin/dashboard/overview */
export interface DashboardOverview {
  conversations: { total: number; active: number; needsHuman: number; today: number };
  messages: { today: number };
  leads: { total: number; today: number; new: number };
  appointments: { requested: number; upcoming7d: number };
  campaigns: { active: number; sendsToday: number };
  alerts: { unresolved: number };
  templates: { total: number; approved: number; draft: number };
  webChats: { total: number; activeToday: number };
  automation: { active: number; total: number; runsToday: number };
  recentConversations: Array<{
    _id: string;
    customerName: string | null;
    customerPhone: string;
    channel: string;
    status: string;
    lastMessage: string;
    lastMessageAt: string | null;
    routingState: 'bot' | 'pending_human' | 'human';
    unreadCount: number;
  }>;
}

/**
 * Who owns the contacts a CSV/Excel import touches. Mirrors ImportAssignmentMode
 * in the backend's contact.service.ts (the two apps share no types package).
 *
 * The mode IS the priority declaration, which is why it is one field rather than
 * a set of flags. Whatever the mode, ownership resolves in this order:
 *
 *   existing owner -> owner column -> single-owner pick -> assignment rules -> unassigned
 *
 * `overwriteExistingOwner` demotes the first rung below the file's own choice —
 * never below the rules, which may claim only a contact nobody owns.
 */
export interface ContactImportAssignment {
  /** 'rules' (default) leaves today's behaviour exactly as it was, and ignores
   *  any owner column in the file. */
  mode: 'rules' | 'column' | 'single' | 'none';
  /** 'single' mode only — the team member to give every contact to. */
  userId?: string;
  /** 'column' mode only. Default true: rows the file names no usable owner for
   *  still get offered to the assignment rules. */
  fallbackToRules?: boolean;
  /** Reassign contacts that already belong to someone else. Owner/admin only. */
  overwriteExistingOwner?: boolean;
}

/** An owner e-mail in the file that could not be used. Grouped per address so a
 *  large file with one bad address returns one entry, not one per row. */
export interface ContactImportUnresolvedOwner {
  email: string;
  reason: 'not_a_user' | 'not_eligible';
  rowCount: number;
  sampleRows: number[];
}

/** A rule whose target disagrees with the owner the FILE names for the same
 *  rows. Preview only — reported so the contradiction is visible before the
 *  import is committed, not weeks later when the rule looks broken. */
export interface ContactImportRuleConflict {
  ruleId: string;
  ruleTargetLabel: string;
  fileTargetLabel: string;
  rowCount: number;
  sampleRows: number[];
}

export interface ContactImportResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; reason: string }>;
  dryRun?: boolean;
  /** Every field below is omitted entirely when it would be zero/empty, so an
   *  import using neither notes nor assignment returns the historical shape. */
  assigned?: number;
  assignSkippedExistingOwner?: number;
  assignAlreadyCorrect?: number;
  notesAdded?: number;
  notesSkippedDuplicate?: number;
  unresolvedOwners?: ContactImportUnresolvedOwner[];
  ruleConflicts?: ContactImportRuleConflict[];
}

export interface ContactNote {
  _id: string;
  text: string;
  authorId: string;
  authorName: string;
  createdAt: string;
  /** Set only on notes that came in through a CSV/Excel import. The UI marks
   *  those "Imported" so the author snapshot is not read as "this person wrote
   *  this, at this moment" — they are the importer, not the observer. */
  source?: 'import';
}

export interface RequestOptions {
  suppressPermissionLock?: boolean;
  /** See tenant-block-detection.util.ts — for background writes that must not pop the read-only dialog. */
  suppressTenantBlocked?: boolean;
}

/** Rolled-up campaign history projected onto the contact (Conversation.model's
 *  ICampaignEngagement). ABSENT entirely on a contact no campaign has ever
 *  targeted — absence IS the "never targeted" signal, so the detail page hides
 *  the whole card rather than rendering a row of zeroes. */
export interface ContactCampaignEngagement {
  targetedCampaignIds?: string[];
  attempts?: number;
  delivered?: number;
  failed?: number;
  failureRatePct?: number;
  firstSentAt?: string;
  lastSentAt?: string;
  lastStatus?: 'sent' | 'delivered' | 'failed';
  lastReadAt?: string;
  failedErrorCodes?: number[];
}

/**
 * One contact's full CRM record — GET /admin/contacts/:id.
 *
 * Backed by the same serializer as a contacts-list row (backend's
 * contact-row.serializer.ts), so every field the list shows is present here
 * verbatim; the extra members below are the detail-page-only additions.
 * `messages` is deliberately absent — the transcript lives in chat logs.
 */
export interface ContactDetail {
  _id: string;
  channel?: 'whatsapp' | 'instagram';
  customerPhone: string;
  customerName?: string;
  customerNames?: string[];
  customerEmail?: string;
  customerCountry?: string;
  customerCountryCode?: string;
  tags?: string[];
  customAttributes?: ContactCustomAttribute[];
  externalAttributes?: ContactExternalAttribute[];
  status: string;
  source?: string;
  sourceDetail?: string;
  messageCount?: number;
  lastMessage?: string;
  lastMessageAt?: string;
  createdAt?: string;
  updatedAt?: string;
  assignedTo?: { _id?: string; firstName?: string; lastName?: string; email?: string } | null;
  // ── detail-only ──────────────────────────────────────────────────────────
  notes?: ContactNote[];
  campaignEngagement?: ContactCampaignEngagement | null;
  optOut?: { isOptedOut?: boolean } | null;
  lastInteractionAt?: string | null;
}

/**
 * One tag/custom-field assignment rule.
 * tags: contact must carry at least one of these tags to match (optional when customFields provided).
 * customFields: contact custom-attribute pairs to match (key exact, value case-insensitive).
 * assignTo: the target — either a specific user or any user who holds one of the given roles.
 * A rule fires when ANY tag matches OR ANY custom-field pair matches (OR semantics).
 */
export interface AssignmentRule {
  id: string;
  tags: string[];
  customFields?: Array<{ key: string; value: string }>;
  assignTo: { kind: 'user'; userId: string } | { kind: 'role'; roleIds: string[] };
}

/** One field-level change on an audit-backed activity item. `from`/`to` are
 *  absent when the key didn't exist before / was removed. */
export interface ContactActivityChangedKey {
  key: string;
  from?: unknown;
  to?: unknown;
}

export type AssignmentActivityAction =
  | 'assignment_assigned'
  | 'assignment_reassigned'
  | 'assignment_unassigned'
  | 'assignment_claimed'
  | 'assignment_escalated'
  | 'assignment_handed_back';

/**
 * One entry in a contact's activity feed (GET /admin/contacts/:id/activity).
 * `note_added` is synthesized from the contact's notes; every `assignment_*`
 * action is synthesized from routing.history (same trail Chat Logs' "Routing
 * history" panel reads); every other action is an AuditEvent action
 * ('created' | 'updated' | 'archived' | 'deleted') on either the contact
 * itself or a lead sharing its phone number.
 */
export interface ContactActivityItem {
  id: string;
  kind: 'note' | 'contact' | 'lead' | 'assignment';
  action: 'note_added' | 'created' | 'updated' | 'archived' | 'deleted' | AssignmentActivityAction;
  at: string;
  /** For a system-initiated assignment row this is a synthetic
   *  {id:'', name:'Assignment rules'|'Automatic escalation'} rather than a
   *  real user — see contact.service.ts's getActivity. */
  actor: { id: string; name: string };
  /** Notes only. */
  text?: string;
  /** Audit rows only — the resource's name as of the change. */
  resourceName?: string;
  /** Audit 'updated' rows only. */
  changedKeys?: ContactActivityChangedKey[];
  /** Free-text audit context. A contact RESTORE is logged as a plain 'updated'
   *  with no changedKeys and reason 'Reactivated', so this is the only thing
   *  that tells it apart from any other edit. */
  reason?: string;
  /** assignment_assigned / assignment_reassigned / assignment_escalated /
   *  assignment_claimed only — who the conversation went to. */
  assigneeName?: string;
  /** assignment_reassigned only — who held the conversation immediately before. */
  previousAssigneeName?: string;
  /** Present (true) only when a HUMAN explicitly re-ran the assignment-rules
   *  engine (the "Apply rules now" button, or a bulk-unassign's "re-apply
   *  rules" flag) and a rule picked `assigneeName` — as opposed to the actor
   *  picking them directly via reassign. Both carry a real `actor`, so
   *  without this flag the two render identically. Absent (not `false`) in
   *  every other case, including the live/automatic trigger (which has no
   *  actor at all — see `actor`'s own doc comment). */
  viaRules?: boolean;
}

/** One appointment row as /admin/appointments serves it. */
export interface AppointmentSummary {
  _id: string;
  source: 'whatsapp' | 'instagram' | 'webchat' | 'manual';
  customer?: { name?: string; phone?: string; email?: string };
  requestedAt?: string;
  requestedText?: string;
  appointmentType?: string;
  notes?: string;
  status: 'requested' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  createdAt: string;
}

interface DashboardMetrics {
  totalConversations: number;
  activeConversations: number;
  totalMessages: number;
  todayMessages: number;
  avgResponseTime: number;
  teamMembers: number;
}

interface Message {
  _id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

/** One sync attempt, from either connector. `sourceId` is present only on
 *  Google Sheets runs, where a single grant covers many sheet tabs and a
 *  history is only readable once attributed to one of them. */
export interface ConnectorSyncRun {
  _id: string;
  sourceId?: string;
  trigger: 'backfill' | 'webhook' | 'reconcile' | 'manual';
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  finishedAt?: string;
  counts: { fetched: number; created: number; updated: number; skipped: number; failed: number };
  errorSummary?: string;
}

/** Kept so the HubSpot card's existing references stay valid — the shape was
 *  never HubSpot-specific, only its name was. */
export type HubSpotSyncRun = ConnectorSyncRun;

export type HubSpotConnectorStatus =
  | { connected: false }
  | {
      connected: true;
      status: 'connected' | 'reauth_required' | 'error' | 'disconnected';
      portalId: string;
      hubDomain?: string;
      hasLeadsAccess: boolean;
      connectedAt: string;
      lastErrorMessage?: string;
      backfill: {
        state: 'not_started' | 'scanning' | 'in_progress' | 'complete' | 'failed';
        cursor?: string;
        total?: number;
        processed: number;
        created: number;
        updated: number;
        startedAt?: string;
        finishedAt?: string;
      };
      lastWebhookEventAt?: string;
      lastReconcileAt?: string;
      recentRuns: HubSpotSyncRun[];
    };

// ─── Google Sheets connector ────────────────────────────────────────────────
// One grant per tenant, many sources beneath it — a source is one spreadsheet
// TAB, which is why a workbook's other tabs can never be silently ignored.

export type GoogleSheetSyncMode = 'full' | 'new_only';

export interface GoogleSheetTab {
  sheetTabId: number;
  title: string;
  columnCount: number;
}

export interface GoogleSheetPreview {
  headers: string[];
  sampleRows: string[][];
  lastColumn: string;
}

export interface GoogleSheetSource {
  _id: string;
  spreadsheetId: string;
  spreadsheetName: string;
  sheetTabId: number;
  sheetTabName: string;
  headerRow: number;
  lastColumn: string;
  /** Header text -> lead field, or `custom:<key>` for a schema-free attribute. */
  mapping: Record<string, string>;
  importTags: string[];
  mode: GoogleSheetSyncMode;
  /** Last spreadsheet row read. The entire sync state is this one integer. */
  cursor: { lastRowIndex: number };
  status: 'active' | 'paused' | 'error';
  lastErrorMessage?: string;
  lastSyncAt?: string;
  counts: { created: number; updated: number; skipped: number; failed: number };
  /** What the most recent sync did — including one that found nothing, which
   *  the run history deliberately does not keep. Absent before the first sync. */
  lastRun?: {
    at: string;
    /** Whether that check was the schedule or a person. Lets the card say
     *  "checked automatically", which the run history cannot show because
     *  scheduled checks finding nothing are deliberately not kept.
     *
     *  Optional because a source that last synced BEFORE this field existed has
     *  no trigger recorded — the schema default only applies on create, so a
     *  lean() read of an older document genuinely returns undefined. The card
     *  stays silent in that case rather than guessing. */
    trigger?: 'manual' | 'scheduled';
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  createdAt: string;
}

export type GoogleSheetsConnectorStatus =
  | { connected: false; sources: [] }
  | {
      connected: true;
      status: 'connected' | 'reauth_required' | 'error' | 'disconnected';
      accountEmail?: string;
      connectedAt: string;
      lastErrorMessage?: string;
      sources: GoogleSheetSource[];
      recentRuns: ConnectorSyncRun[];
    };

export interface ContactCustomAttribute {
  key: string;
  value: string | number | boolean;
  updatedAt?: string;
}

export interface ContactExternalAttribute {
  clientId: string;
  clientName: string;
  attributes: Array<{ key: string; value: any }>;
  tags?: string[];
  externalContactId?: string;
  lastSyncedAt?: string;
}

/** Which reply mode this conversation is currently in. Absence (undefined) means
 *  the doc predates hybrid routing — treated identically to 'bot'. */
export type ConversationRoutingState = 'bot' | 'pending_human' | 'human';

export interface ConversationRouting {
  /** Absent for an Instagram thread — Instagram has no bot yet, so there is
   *  no bot/pending_human/human state to distinguish (see
   *  InstagramConversation.model.ts's IInstagramRouting doc comment). Every
   *  WhatsApp conversation still always has one. */
  state?: ConversationRoutingState;
  assignedAt?: string;
  assignedBy?: string;
  assignmentMethod?: 'round_robin' | 'role_round_robin' | 'owner_fallback' | 'claim' | 'manual';
  escalation?: {
    triggerType: 'explicit_request' | 'low_confidence';
    triggeredAt: string;
    /** Why the bot handed over — `matchedPhrase` is the customer's own words that
     *  tripped a tenant/built-in trigger phrase. */
    context?: {
      agentId?: string;
      confidence?: number;
      reason?: string;
      matchedPhrase?: string;
      phraseSource?: 'builtin' | 'tenant';
    };
  };
  /** Append-only audit trail. Present on getConversationDetail only — the list
   *  endpoint strips it to keep the page payload small. */
  history?: RoutingHistoryEntry[];
}

/** Tenant-configured bot→human escalation (Tenant.settings.routing.escalation).
 *  Always returned fully resolved by the API — absence-is-default is applied
 *  server-side, so nothing here is optional on read. */
export interface EscalationSettings {
  /** Master switch over BOTH automatic triggers (keyword request and repeated low
   *  confidence). Manual claim/assign from the inbox is unaffected. */
  enabled: boolean;
  keywords: string[];
  /** Whether `keywords` layers on top of the built-in English phrases ('extend') or
   *  stands in for them ('replace'). */
  mode: 'extend' | 'replace';
  /** Phrases that suppress escalation even when a keyword matches. */
  exclusions: string[];
  /** Confidence at or below which two consecutive weak bot turns hand over. Separate
   *  from the RAG agent-selection threshold — a different decision. */
  lowConfidenceThreshold: number;
  /** Blank means "use the built-in default" (see RoutingSettingsResponse.defaults). */
  handoffMessage: string;
  /** Sent when the AI can't generate any reply (full pipeline failure / timeout).
   *  Blank means "use the built-in default" (see RoutingSettingsResponse.defaults). */
  aiFailureMessage: string;
  /** Who an automatic handoff (keyword / low-confidence / AI tool) is assigned to.
   *  `{kind:'anyone'}` is the pre-existing behaviour — round-robin over every
   *  eligible team member. A "Send to a Team Member" automation node may set its
   *  own override for that one flow, taking precedence over this default. */
  assignmentTarget: RoutingTarget;
}

/** Who an automatic handoff should be assigned to — shared shape between the
 *  tenant-wide default (EscalationSettings.assignmentTarget) and a workflow
 *  node's own override (WorkflowCanvas's human_handoff node data). */
export type RoutingTarget = { kind: 'anyone' } | { kind: 'role'; roleIds: string[] };

/** Settings for the public web-chat widget (Tenant.settings.leadForm). */
export interface LeadFormSettings {
  /** AI agent driving the chat; null = the scripted fallback flow. */
  agentId: string | null;
  /** Origins allowed to embed/call the widget; empty = unrestricted. */
  allowedOrigins: string[];
  /** Display name in the widget header. '' = fall back to the org name. */
  botName: string;
  /** First message shown to a visitor. '' = the widget's built-in greeting. */
  welcomeMessage: string;
  /** Sub-line under the header title. '' = "Typically replies instantly". */
  statusText: string;
}

export interface RoutingSettingsResponse {
  escalation: EscalationSettings;
  /** Illustrative examples of what 'extend' mode already covers — read-only. */
  builtinExamples: string[];
  defaults: { handoffMessage: string; aiFailureMessage: string; lowConfidenceThreshold: number };
  limits: {
    maxKeywords: number;
    maxKeywordLength: number;
    maxHandoffMessageLength: number;
    maxAiFailureMessageLength: number;
    maxAssignmentRoleIds: number;
  };
}

/** Why the escalation evaluator did or didn't fire — mirrors the backend's
 *  EscalationDecisionReason. */
export type EscalationTestReason =
  | 'disabled'
  | 'excluded'
  | 'matched_builtin'
  | 'matched_tenant'
  | 'no_match';

export interface RoutingPhraseTestResult {
  wouldEscalate: boolean;
  reason: EscalationTestReason;
  matchedPhrase?: string;
  phraseSource?: 'builtin' | 'tenant';
  excludedBy?: string;
}

/** One entry of a conversation's routing audit trail (Conversation.routing.history).
 *  Detail-view only — the conversations list strips it. */
export interface RoutingHistoryEntry {
  type: 'escalated' | 'assigned' | 'claimed' | 'handed_back';
  occurredAt: string;
  actorId?: string;
  targetUserId?: string;
  method?: 'round_robin' | 'role_round_robin' | 'owner_fallback' | 'claim' | 'manual' | 'rule_manual';
  trigger?: {
    triggerType: 'explicit_request' | 'low_confidence';
    matchedPhrase?: string;
    phraseSource?: 'builtin' | 'tenant';
    confidence?: number;
    agentId?: string;
    /** Present only when `method` is 'role_round_robin' or 'owner_fallback' — the
     *  configured handoff role's ids, for "why did this go to X?" context. */
    targetRoleIds?: string[];
  };
  /** Resolved server-side alongside the raw ids; null when the user no longer exists. */
  actor?: { _id: string; firstName?: string; lastName?: string; email?: string } | null;
  targetUser?: { _id: string; firstName?: string; lastName?: string; email?: string } | null;
}

interface Conversation {
  _id: string;
  channel?: 'whatsapp' | 'instagram';
  channelIdentity?: { type: string; value: string };
  customerPhone: string;
  customerPhoneNormalized?: string;
  customerName?: string;
  messageCount: number;
  lastMessage: string;
  lastMessageAt: string;
  status: string;
  tags?: string[];
  unreadCount?: number;
  assignedTo?: any;
  /** Absent means the current assignee hasn't opened it yet — the "New" badge's
   *  data source. Present on both channels. */
  assigneeSeenAt?: string | null;
  routing?: ConversationRouting;
  messages?: Message[]; // Populated in detail view
  customAttributes?: ContactCustomAttribute[];
  windowExpiresAt?: string;
  lastInboundMessageAt?: string;
  source?: string;
  /** Drill-down for `source` — e.g. the external client's name for source==='api'. */
  sourceDetail?: string;
  externalAttributes?: ContactExternalAttribute[];
  /** Detail view only — the list's serializer projects just `metadata.tags`
   *  (as top-level `tags`), so click-to-WhatsApp ad attribution only ever
   *  arrives via getConversationDetail, which returns the raw doc. */
  metadata?: {
    ctwaAttribution?: {
      sourceUrl?: string;
      sourceId?: string;
      headline?: string;
      capturedAt?: string;
      sourceType?: string;
      body?: string;
      mediaType?: string;
      ctwaClid?: string;
    };
  };
}

interface LLMModelEntry {
  id: string;
  name: string;
  provider: string;
  isEnabled: boolean;
  deactivatedAt?: string;
  deactivatedReason?: string;
}

interface LLMConfig {
  apiKey: string;
  temperature: number;
  maxTokens: number;
  availableModels: LLMModelEntry[];
}

export interface RagVectorConfig {
  configured: boolean;
  indexName?: string;
  environment?: string;
  isActive: boolean;
  lastValidatedAt?: string;
  lastValidationStatus: 'valid' | 'invalid' | 'unchecked';
  lastValidationError?: string;
  ragEnabled: boolean;
  instagramRagEnabled?: boolean;
  playgroundRagEnabled?: boolean;
  widgetRagEnabled?: boolean;
}

export interface RagVectorTestResult {
  valid: boolean;
  error?: string;
}

export interface FieldDefinition {
  _id: string;
  // 'flow' = the workspace flow-variable registry (reusable {{variable}} names an
  // automation reads/writes). Carries no stored value — advisory naming only.
  entity: 'lead' | 'contact' | 'flow';
  key: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'enum';
  /** Optional note — what this field/variable holds. */
  description?: string;
  options?: string[];
  required?: boolean;
  /** Lead fields only: the core field this one stands in for when scoring (null clears it). */
  mapsTo?: 'interest' | 'budget' | 'estimatedValue' | null;
  source: 'vertical' | 'custom';
  order: number;
  createdAt?: string;
}

export interface VerticalContext {
  vertical: string;
  terminology: { lead: { singular: string; plural: string } };
  /** Per-entity cap on custom field definitions (0 = unlimited). */
  fieldDefinitionsLimit?: number;
}

export type ScoringCondition =
  | 'exists'
  | 'equals'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'in';

export type ScoringGroup = 'fit' | 'engagement' | 'pipeline';

export interface ScoreBreakdownEntry {
  label: string;
  points: number;
  group: ScoringGroup | 'decay';
}

export interface ScoringRuleInput {
  group?: ScoringGroup;
  field: string;
  condition: ScoringCondition;
  value?: unknown;
  score: number;
  description?: string;
}

export interface LeadScoringConfig {
  rules: ScoringRuleInput[];
  priorityThresholds: { hot: number; high: number; medium: number };
  recencyDecayPerDay: number;
  /** true = this tenant has its own overrides; false = inheriting the platform default. */
  isCustom?: boolean;
  /** Platform default rules this workspace's saved copy doesn't have (platform changed since it was saved). */
  missingDefaults?: ScoringRuleInput[];
}

export interface TeamMember {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  lastLoginAt?: string;
}

/** Mirrors the backend's ISegmentFilterParams (Segment.model.ts) — the saved,
 *  reusable filter spec a "dynamic segment" persists. */
/**
 * A bulk selection: either rows the user ticked by hand, or the FULL filter
 * spec describing "everything matching the current view".
 *
 * The filter half is deliberately open-ended rather than an enumerated list of
 * fields. The backend resolves it through the same builder the contacts list
 * itself runs on, so the set of valid keys IS the set of list filters — pinning
 * a narrower type here is what previously let bulk silently ignore filters it
 * had not been taught about. `confirmed: true` is mandatory on the filter half
 * and enforced server-side: it resolves to rows the caller never enumerated.
 */
export type ContactBulkSelection =
  | { ids: string[] }
  | (Record<string, unknown> & { confirmed: true });

/** One row of the contact audit trail. Mirrors the backend `IAuditEvent`
 *  minus `snapshot` (projected out server-side — it holds a whole pre-delete
 *  document per row), plus the resolved `actorName`. */
export interface ContactAuditEvent {
  _id: string;
  action: 'created' | 'updated' | 'archived' | 'deleted';
  actor: string;
  actorName: string;
  resourceId?: string;
  resourceName?: string;
  /** Per-field diff — present on non-bulk 'updated' rows only. */
  changedKeys?: Array<{ key: string; from?: unknown; to?: unknown }>;
  /** True for a one-row-per-operation bulk summary; `affectedCount`/`reason`
   *  carry what happened instead of `resourceId`. */
  isBulk?: boolean;
  affectedCount?: number;
  reason?: string;
  createdAt: string;
}

export interface SegmentFilterParams {
  status?: string;
  tags?: string[];
  advanced?: string;
  errorCode?: number;
  campaignId?: string;
  contactSource?: string;
  dateAddedFrom?: string;
  dateAddedTo?: string;
  engagementTier?: string;
  /** Raw recency range — mutually exclusive with engagementTier on the backend.
   *  No UI control exposes this yet (only the engagementTier preset is surfaced
   *  in Contacts.tsx), but it's part of the wire contract and must stay mirrored. */
  recencyFrom?: string;
  recencyTo?: string;
  /** "Last message" (either direction) range — resolves against lastInteractionAt. */
  lastMessageFrom?: string;
  lastMessageTo?: string;
  /** "Last note added" range — resolves against the denormalized lastNoteAt. */
  lastNoteFrom?: string;
  lastNoteTo?: string;
  frequencyMin?: number;
  frequencyMax?: number;
  attributes?: Array<{ key: string; value: string | number | boolean }>;
  matchMode?: 'all' | 'any';
  campaignSentFrom?: string;
  campaignSentTo?: string;
  assignedTo?: string;
  minFailureRate?: number;
  minAttempts?: number;
}

export interface Segment {
  _id: string;
  name: string;
  description?: string;
  filterParams: SegmentFilterParams;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface QASource {
  _id: string;
  title: string;
  questions: string[];
  answer: string;
  createdByName: string;
  updatedByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TextSource {
  _id: string;
  title: string;
  textContent: string;
  createdByName: string;
  updatedByName?: string;
  createdAt: string;
  updatedAt: string;
}

interface FileSource {
  _id: string;
  name: string;
  path: string;
  uniqueName: string;
  originalName: string;
  mimeType: string;
  size: number;
  s3Url: string;
  createdByName: string;
  updatedByName?: string;
  createdAt: string;
  updatedAt: string;
}

export type MediaAssetType = 'document' | 'image' | 'video' | 'audio';

export interface MediaAsset {
  _id: string;
  name: string;
  description?: string;
  folderId?: string | null;
  type: MediaAssetType;
  s3Key: string;
  s3Url: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Saved interactive messages (List / Button) ─────────────────────────────

export type InteractiveMessageKind = 'list' | 'button';

/**
 * How the message's BODY is produced at send time — scoped to body only;
 * header/footer always stay `{{n}}`-templated regardless, and row/button
 * titles never vary at all (they're the routing contract).
 *
 * - `'variable'` (default): `bodyText` is the literal wording, with optional
 *   `{{n}}` slots filled from `variables` at send time.
 * - `'dynamic'`: `bodyText` holds AUTHORING GUIDANCE for the AI, never sent
 *   verbatim — the model writes the real body itself, grounded in the
 *   conversation. AI-only: requires `aiEnabled: true`, and a workflow-canvas
 *   node (which has no free text to offer) cannot reference one.
 */
export type InteractiveMessageBodyMode = 'variable' | 'dynamic';

export interface InteractiveMessageRow {
  /** Minted server-side, stable forever. Echo it back on save or the server
   *  treats the row as new and mints a fresh one — which would break any
   *  trigger branch already wired to the old id. */
  id?: string | undefined;
  title: string;
  description?: string | undefined;
}

export interface InteractiveMessageSection {
  title?: string | undefined;
  rows: InteractiveMessageRow[];
}

export interface InteractiveMessageButton {
  id?: string | undefined;
  title: string;
}

export interface InteractiveMessageMediaHeader {
  type: 'image' | 'video' | 'document';
  mediaAssetId: string;
}

/** What the editor submits. The server derives `variableCount` and mints any
 *  missing option ids, so neither is sent. */
export interface InteractiveMessageInput {
  kind: InteractiveMessageKind;
  name: string;
  description?: string | undefined;
  /** May an AI agent send this? Opt-in — the server defaults it to false. */
  aiEnabled?: boolean | undefined;
  /** Defaults to 'variable' server-side when omitted. */
  bodyMode?: InteractiveMessageBodyMode | undefined;
  /** 'variable' mode: literal wording, may contain {{n}}. 'dynamic' mode:
   *  authoring guidance for the AI, never sent to a customer verbatim. */
  bodyText: string;
  headerText?: string | undefined;
  footerText?: string | undefined;
  buttonText?: string | undefined;
  sections?: InteractiveMessageSection[] | undefined;
  buttons?: InteractiveMessageButton[] | undefined;
  header?: InteractiveMessageMediaHeader | undefined;
}

export interface InteractiveMessage extends InteractiveMessageInput {
  _id: string;
  aiEnabled: boolean;
  bodyMode: InteractiveMessageBodyMode;
  /** How many `{{n}}` slots the text fields declare — derived server-side. */
  variableCount: number;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

/** Meta's own caps, mirrored from the server's whatsapp-interactive-message.util.ts
 *  so the editor can show live counters instead of only failing at save. */
export const INTERACTIVE_MESSAGE_LIMITS = {
  listMaxSections: 10,
  listMaxRowsTotal: 10,
  rowTitleMax: 24,
  rowDescriptionMax: 72,
  listButtonTextMax: 20,
  replyButtonMax: 3,
  replyButtonTitleMax: 20,
} as const;

/** Meta's per-category ceiling — mirrors the server's own limits module. Used
 *  only to explain, in the picker, why a library file cannot be attached. */
export const WHATSAPP_MEDIA_SIZE_LIMIT_BYTES: Record<MediaAssetType, number> = {
  document: 100 * 1024 * 1024,
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  audio: 16 * 1024 * 1024,
};

/** Instagram's own per-category ceiling — mirrors instagram-media-limits.util.ts
 *  server-side. Independently declared, not derived from WHATSAPP_MEDIA_SIZE_
 *  LIMIT_BYTES above: the two platforms' real limits differ (image 8MB vs 5MB,
 *  audio/video/document 25MB vs 16MB/16MB/100MB). */
export const INSTAGRAM_MEDIA_SIZE_LIMIT_BYTES: Record<MediaAssetType, number> = {
  document: 25 * 1024 * 1024,
  image: 8 * 1024 * 1024,
  video: 25 * 1024 * 1024,
  audio: 25 * 1024 * 1024,
};

export interface MediaFolder {
  _id: string;
  name: string;
  parentId?: string | null;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MediaBrowseResult {
  folders: MediaFolder[];
  assets: MediaAsset[];
  /** Breadcrumb from root down to the current folder (empty at root). */
  path: Array<{ _id: string; name: string }>;
}

interface Agent {
  _id: string;
  name: string;
  topics?: string[];
}

const ensureArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const normalizeKnowledgeBase = (knowledgeBase: any = {}) => {
  const files = ensureArray(knowledgeBase?.files);
  const sources = {
    files: ensureArray(knowledgeBase?.sources?.files),
    texts: ensureArray(knowledgeBase?.sources?.texts),
    qas: ensureArray(knowledgeBase?.sources?.qas),
  };
  const sourceRefs = {
    fileIds: ensureArray(knowledgeBase?.sourceRefs?.fileIds),
    textIds: ensureArray(knowledgeBase?.sourceRefs?.textIds),
    qaIds: ensureArray(knowledgeBase?.sourceRefs?.qaIds),
  };

  const counts = {
    ...knowledgeBase?.counts,
    attachedFileCount:
      knowledgeBase?.counts?.attachedFileCount ?? sources.files.length ?? sourceRefs.fileIds.length,
    attachedTextCount:
      knowledgeBase?.counts?.attachedTextCount ?? sources.texts.length ?? sourceRefs.textIds.length,
    attachedQaCount:
      knowledgeBase?.counts?.attachedQaCount ?? sources.qas.length ?? sourceRefs.qaIds.length,
    totalAttachedCount:
      knowledgeBase?.counts?.totalAttachedCount ??
      sources.files.length + sources.texts.length + sources.qas.length,
  };

  return {
    ...knowledgeBase,
    files,
    textContent: typeof knowledgeBase?.textContent === 'string' ? knowledgeBase.textContent : '',
    sourceRefs,
    sources,
    counts,
  };
};

const normalizeAgent = (agent: any) => {
  if (!agent || typeof agent !== 'object') {
    return agent;
  }

  // Empty means "uses the system default model" — no hardcoded model name here.
  const llmModel = agent.llmModel || agent.model || '';
  return {
    ...agent,
    model: llmModel,
    llmModel,
    knowledgeBase: normalizeKnowledgeBase(agent.knowledgeBase),
  };
};

const normalizeAgentPayload = (payload: any) => {
  if (!payload) {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload.map(normalizeAgent);
  }

  if (payload.agent) {
    return {
      ...payload,
      agent: normalizeAgent(payload.agent),
    };
  }

  return normalizeAgent(payload);
};

interface WhatsAppConfig {
  phoneNumberId: string;
  phoneNumber: string;
  displayPhoneNumber: string;
  wabaId: string;
  accessToken?: string;
  metaAppId: string;
  metaAppSecret?: string;
  webhookVerifyToken: string;
  /** Present on GET /config — secrets are not returned; use this for UI hints */
  hasAccessToken?: boolean;
  hasMetaAppSecret?: boolean;
  setupComplete?: boolean;
  /** 'expired' once Meta rejects the stored access token (OAuthException 190) */
  tokenStatus?: 'valid' | 'expired';
  tokenStatusDetail?: string;
  tokenStatusUpdatedAt?: string;
}

/** Result of an on-demand "Check Now" pull of quality rating + tier from Meta. */
export interface WhatsAppTierStatus {
  qualityRating: string | null;
  rawTier: string | null;
  messagingTierLimit: number;
  syncedAt: string | null;
  capacity: { limit: number; used: number; remaining: number };
}

export interface InstagramConfig {
  /** False only when no InstagramConfig row exists for this tenant yet. */
  exists?: boolean;
  isConnected: boolean;
  /** True once connecting is possible: always true in platform mode (the shared
   *  app is ready); in BYO mode, once the tenant's four app-credential fields
   *  are all saved. */
  appConfigComplete?: boolean;
  /** Which Meta-app credential model this tenant uses. 'platform' (the managed
   *  shared app, primary/default) needs no tenant credentials; 'byo' uses the
   *  tenant's own app. Decided by a super-admin. */
  authMode?: 'platform' | 'byo';
  /** Whether the shared platform app is configured on the server — false means
   *  a platform-mode tenant can't connect until an admin sets it up. */
  platformConfigured?: boolean;
  /** Super-admin-only: when true, the tenant-facing Settings tab is hidden. */
  tenantUiHidden?: boolean;
  appId?: string;
  oauthRedirectUri?: string;
  webhookVerifyToken?: string;
  hasAppSecret?: boolean;
  igUserId?: string;
  username?: string;
  name?: string;
  profilePictureUrl?: string;
  followersCount?: number;
  mediaCount?: number;
  /** 'expired' once Meta rejects the stored access token (OAuthException 190) */
  tokenStatus?: 'valid' | 'expired';
  tokenStatusDetail?: string;
  tokenExpiresAt?: string;
  /** Existing Instagram conversations under this tenant, present only while
   *  connected — surfaced so the "Switch account" confirmation can name a
   *  real number. There is only ONE InstagramConfig row per tenant, and
   *  InstagramConversation is keyed by {tenantId, igsid} with no per-account
   *  scoping, so switching accounts does NOT migrate or isolate this history:
   *  every existing thread stays in the same collection, unseparated. */
  conversationCount?: number;
}

/** Phase 2.6 — VERIFIED shape matching Meta's own ice-breakers API exactly
 *  (question/payload pairs), so no translation happens between this and the
 *  wire format. */
export interface InstagramIceBreaker {
  question: string;
  payload: string;
}

/** Phase 2.6 — VERIFIED shape matching Meta's own persistent-menu API. */
export interface InstagramPersistentMenuItem {
  title: string;
  type: 'postback' | 'web_url';
  payload?: string;
  url?: string;
}

/* ── Phase 3: Instagram comments + comment-to-DM automation ─────────────── */

export interface InstagramCommentRow {
  _id: string;
  commentId: string;
  mediaId: string;
  mediaProductType?: string;
  parentCommentId?: string;
  from: { id: string; username?: string };
  text: string;
  isHidden: boolean;
  isDeleted: boolean;
  publicReplyCommentId?: string;
  publicRepliedAt?: string;
  privateReplySentAt?: string;
  privateReplyMessageId?: string;
  matchedRuleId?: string;
  createdAt: string;
}

export interface InstagramMentionRow {
  _id: string;
  mentionType: 'comment_mention' | 'caption_mention';
  mediaId: string;
  commentId?: string;
  text?: string;
  fromUsername?: string;
  permalink?: string;
  capturedAt: string;
}

export type InstagramCommentRuleScope =
  | { type: 'all' }
  | { type: 'specific_posts'; mediaIds: string[] };

export type InstagramCommentModerationAction = 'none' | 'hide' | 'delete';

export interface InstagramCommentRule {
  _id: string;
  name: string;
  enabled: boolean;
  scope: InstagramCommentRuleScope;
  keywords?: string[];
  publicReplyTemplate?: string;
  /** Optional since auto-moderation (Phase 3.4) — a moderation-only rule has no DM. */
  privateReplyTemplate?: string;
  moderationAction?: InstagramCommentModerationAction;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export interface InstagramCommentRuleInput {
  name: string;
  enabled?: boolean;
  scope: InstagramCommentRuleScope;
  keywords?: string[];
  // `| undefined` is explicit (repo tsconfig has exactOptionalPropertyTypes) —
  // the form builds these keys as `trimmed || undefined`.
  publicReplyTemplate?: string | undefined;
  privateReplyTemplate?: string | undefined;
  moderationAction?: InstagramCommentModerationAction;
  priority?: number;
}

// Fields explicitly typed `| undefined` (not just `?:`) — this repo's tsconfig
// sets exactOptionalPropertyTypes, and the tenant-facing/saas-front forms
// build this shape from API responses that may omit any field, then hold it
// in useState and re-spread it, so the "present but undefined" case is real.
export interface PrivacyPolicyFields {
  mode?: 'custom' | 'platform_hosted' | undefined;
  customUrl?: string | undefined;
  legalBusinessName?: string | undefined;
  category?: string | undefined;
  address?: string | undefined;
  contactEmail?: string | undefined;
  dpoName?: string | undefined;
  dpoEmail?: string | undefined;
  version?: number | undefined;
  publishedAt?: string | undefined;
}

/** An Android app registered for ONE_TAP/ZERO_TAP autofill (Meta allows up to 5). */
export interface WhatsAppTemplateSupportedApp {
  package_name: string;
  /** The app's signing-key signature hash — Meta requires exactly 11 characters. */
  signature_hash: string;
}

export interface WhatsAppTemplateButton {
  /** `OTP` is the AUTHENTICATION-only button; `COPY_CODE` here is the MARKETING "copy offer code" button. */
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER' | 'COPY_CODE' | 'OTP';
  text: string;
  url?: string;
  phone_number?: string;
  /** Sample code shown to Meta for review when type is COPY_CODE */
  example?: string;
  /** Set when type is OTP — which authentication delivery method this button represents. */
  otp_type?: 'COPY_CODE' | 'ONE_TAP' | 'ZERO_TAP';
  /** ONE_TAP/ZERO_TAP only — label for the autofill button. */
  autofill_text?: string;
  /** ONE_TAP/ZERO_TAP only — Android apps eligible to receive the autofill broadcast. */
  supported_apps?: WhatsAppTemplateSupportedApp[];
  /** ZERO_TAP only — must be `true` to submit. */
  zero_tap_terms_accepted?: boolean;
}

export interface WhatsAppTemplateNamedParam {
  param_name: string;
  example?: string;
}

export interface WhatsAppTemplateComponents {
  header?: {
    type: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
    text?: string;
    example?: {
      header_text?: string[];
      header_handle?: string[];
      header_text_named_params?: WhatsAppTemplateNamedParam[];
    };
    /** Author-assigned label for the header's `{{1}}` variable, e.g. "Customer name". */
    headerVariableLabel?: string;
    /** Publicly-accessible URL used as the header media `link` when sending (fallback). */
    headerMediaUrl?: string;
    /** Private S3 key of the uploaded header media, used to obtain a Meta media id. */
    headerMediaS3Key?: string;
    headerMediaMimeType?: string;
    headerMediaFileName?: string;
    /** Meta media id (`{ id: ... }`), the industry-standard way to send media headers. */
    headerMediaId?: string;
    headerMediaIdUpdatedAt?: string;
  };
  /** Required for MARKETING/UTILITY. Absent for AUTHENTICATION — Meta auto-generates the body. */
  body?: {
    text: string;
    example?: {
      body_text?: string[][];
      body_text_named_params?: WhatsAppTemplateNamedParam[];
    };
    /** Author-assigned labels for each `{{n}}` variable (index 0 = `{{1}}`), e.g. "Order ID". */
    variableLabels?: string[];
  };
  footer?: { text: string };
  buttons?: WhatsAppTemplateButton[];
  /** AUTHENTICATION only. */
  authConfig?: {
    /** Appends "For your security, do not share this code." to the body. */
    addSecurityRecommendation?: boolean;
    /** Appends "This code expires in <n> minutes." to the footer. Meta range: 1-90. */
    codeExpirationMinutes?: number;
  };
  /** MARKETING/UTILITY carousel: 1–10 cards, each with its own media header + body
   *  + up to 2 buttons. All cards must share one structure. Mutually exclusive with
   *  a top-level media header (the template's intro is body-only). */
  carousel?: {
    cards: WhatsAppTemplateCarouselCard[];
  };
}

export interface WhatsAppTemplateCarouselCard {
  header: {
    type: 'IMAGE' | 'VIDEO';
    example?: { header_handle?: string[] };
    headerMediaUrl?: string;
    headerMediaS3Key?: string;
    headerMediaMimeType?: string;
    headerMediaFileName?: string;
    /** Cached Meta media id for this card (returned by the API; mirrors the backend). */
    headerMediaId?: string;
    headerMediaIdUpdatedAt?: string;
  };
  body: { text: string; example?: { body_text?: string[][] } };
  buttons?: WhatsAppTemplateButton[];
}

export interface WhatsAppTemplateStatusEvent {
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAUSED' | 'DISABLED';
  reason?: string;
  source: 'submit' | 'sync' | 'webhook' | 'disabled-sweep' | 'draft';
  metaTemplateId?: string;
  occurredAt: string;
}

export interface WhatsAppTemplate {
  _id: string;
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAUSED' | 'DISABLED';
  components: WhatsAppTemplateComponents;
  /** AUTHENTICATION only — 30-900 seconds. */
  messageSendTtlSeconds?: number;
  /** Meta's parameter format for this template: positional `{{1}}` (default) or named `{{name}}`. */
  parameterFormat?: 'POSITIONAL' | 'NAMED';
  qualityScore?: string;
  rejectedReason?: string;
  /**
   * Internal media-serviceability flag (independent of Meta `status`). When
   * 'needs_reupload', the template's header/card media can no longer be resolved
   * and all sends are blocked until the media is re-uploaded — fixed on the
   * "Fix media" screen, no Meta re-approval. Absent == healthy.
   */
  mediaHealth?: 'ok' | 'needs_reupload';
  mediaHealthReason?: string;
  mediaHealthDetectedAt?: string;
  /** Append-only history of status transitions (submit/sync/webhook/disable), most recent last. */
  statusHistory?: WhatsAppTemplateStatusEvent[];
  lastSyncedAt?: string;
  createdAt: string;
}

interface SubscriptionPlan {
  _id?: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  yearlyPrice?: number;
  currency: string;
  billingCycle: 'monthly' | 'yearly' | 'both';
  features: {
    messagesLimit: number;
    storageLimit: number;
    apiCallsLimit: number;
    usersLimit: number;
    agentsLimit: number;
    whatsappEnabled: boolean;
    llmConfigEnabled: boolean;
    customBranding: boolean;
    prioritySupport: boolean;
    sslEnabled: boolean;
    apiAccess: boolean;
    webhooksEnabled: boolean;
    campaignsEnabled: boolean;
    whatsapp24hMessageLimit: number;
    campaignMaxRecipientsDefault: number;
    campaignDailyLimitDefault: number;
    campaignBatchSize: number;
    campaignBatchDelayMs: number;
    aiMaxTokensPerResponse: number;
    ragTopK: number;
    ragTopKRerank: number;
    ragScoreThreshold: number;
    contactMaxCustomAttributes: number;
    contactMaxAttributeKeyLength: number;
    contactMaxAttributeValueLength: number;
    contactCsvImportMaxRows: number;
    externalApiBulkUpsertMaxContacts: number;
    externalApiDefaultRateLimitPerMinute: number;
    externalApiDefaultBurstAllowance: number;
    externalApiKeyMaxFailedAttempts: number;
    externalApiKeyLockoutMs: number;
    workflowMaxConcurrentExecutionsDefault: number;
    workflowDeduplicationWindowSecondsDefault: number;
    workflowMaxRetriesDefault: number;
    workflowMaxNestingDepth: number;
    templateStatusHistoryLimit: number;
    maxUploadBodySizeMb: number;
  };
  isActive: boolean;
  isPopular?: boolean;
  sortOrder: number;
}

export interface PlanLimitStatus {
  used: number;
  limit: number;
  remaining: number;
  exceeded: boolean;
  usagePercent: number;
  unbounded: boolean;
  /** Inbound media only: bytes a full store REFUSED, and that freeing space
   *  would recover. Deliberately not folded into `used`, which counts bytes
   *  actually held — this is what explains a "storage full" message sitting
   *  next to a meter that still shows room left. */
  skippedBytes?: number;
  /** How many attachments `skippedBytes` covers. */
  skippedCount?: number;
}

export interface InboundMediaItem {
  messageId: string;
  attachmentIndex: number;
  conversationId: string;
  customerPhone?: string;
  customerName?: string;
  messageType?: string;
  mimeType?: string;
  size: number;
  /** Over-quota capture: the original was never stored, and this is what
   *  freeing space would bring back. `size` stays 0 — it counts held bytes. */
  skippedBytes?: number;
  /** Why a queued download has not landed — set for non-transient failures. */
  error?: string;
  storageState?: string;
  createdAt: string;
  expiresAt?: string;
  promoted: boolean;
  legalHold: boolean;
  /** Present only for callers with `activity.media` read. */
  thumbUrl?: string;
  /** Present only for callers with `activity.media` edit. */
  s3Url?: string;
}

export interface InboundMediaPage {
  items: InboundMediaItem[];
  nextCursor?: string;
  /** Whether this caller may open full-size originals — drives the UI so a
   *  read-only agent is not shown an action that would 403. */
  canOpenOriginal: boolean;
}

export interface BillingUsageStatus {
  messages: PlanLimitStatus;
  storage: PlanLimitStatus;
  /** Inbound customer media (WhatsApp attachments). Optional so a frontend
   *  deployed ahead of the backend does not crash on an older payload. */
  conversationMedia?: PlanLimitStatus;
  apiCalls: PlanLimitStatus;
  users: PlanLimitStatus;
  agents: PlanLimitStatus;
  contacts: PlanLimitStatus;
  aiTokens: PlanLimitStatus;
  roles: PlanLimitStatus;
}

interface BillingActivePlanResponse {
  activePlan: SubscriptionPlan;
  otherPlans: SubscriptionPlan[];
  tenant: {
    planType: 'free' | 'pro' | 'enterprise';
    status: 'active' | 'inactive' | 'suspended' | 'cancelled' | 'trial';
    /** Paid-entitlement window. Absent currentPeriodStart/End means either
     *  free tier (never paid) or a permanent super-admin grant (no expiry). */
    billing: {
      currentPeriodStart?: string;
      currentPeriodEnd?: string;
      renewalCount: number;
      status: 'active' | 'expired' | 'none';
    };
  };
  usage: BillingUsageStatus;
}

interface BillingUsageLimitsResponse {
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  message: PlanLimitStatus;
  storage: PlanLimitStatus;
  apiCalls: PlanLimitStatus;
  users: PlanLimitStatus;
  agents: PlanLimitStatus;
  isAnyLimitExceeded: boolean;
}

export interface DailyUsagePoint {
  date: string;
  messagesUsed: number;
  apiCallsUsed: number;
  aiTokensUsed: number;
  aiCostUsd: number;
  aiRequestCount: number;
}

/** The window a usage response covers, and the tenant's live billing period. */
export interface UsageWindowInfo {
  window?: { startDate: string; endDate: string; source: 'billing_period' | 'custom' };
  currentPeriod?: { start: string; end: string };
}

export interface UsageDailyResponse extends UsageWindowInfo {
  series: DailyUsagePoint[];
  summary: {
    messagesUsed: number;
    apiCallsUsed: number;
    aiTokensUsed: number;
    aiCostUsd: number;
    aiRequestCount: number;
    modelBreakdown: Array<{ model: string; tokens: number; cost: number }>;
  };
}

export interface LLMUsageResponse extends UsageWindowInfo {
  tokensUsed: number;
  totalCost: number;
  requestCount: number;
  aiMessageCount: number;
  avgTokensPerRequest: number;
  modelBreakdown: Array<{ model: string; tokens: number; cost: number }>;
}

/** One day's WhatsApp message counts, split by the Meta template category that
 *  determines what Meta charges us. `total` can exceed the sum of the four
 *  named categories because it also includes `unknown` (messages whose
 *  category we never resolved). */
export interface WhatsAppMessageDay {
  date: string;
  marketing: number;
  utility: number;
  authentication: number;
  service: number;
  unknown: number;
  total: number;
  billable: number;
  /** Conversation-based-pricing (CBP) units billed this day — Meta charges
   *  once per 24h conversation window, not once per message, so this is a
   *  SEPARATE count from `billable` (message-based), not a subset of it. Only
   *  ever non-zero for a tenant/window still on CBP. */
  billedConversations: number;
}

export interface WhatsAppMessageUsage {
  range: {
    startDate: string;
    endDate: string;
    /** How the server picked the window: from the tenant's billing period,
     *  from the 30-day fallback, or straight from the query params we sent. */
    source: 'billing_period' | 'default_30d' | 'query';
  };
  totals: {
    marketing: number;
    utility: number;
    authentication: number;
    service: number;
    unknown: number;
    total: number;
    /** Per-message-priced (PMP) messages Meta actually charges for — Meta's
     *  own `billable` flag where the delivery webhook confirmed it, otherwise
     *  inferred from category. Deliberately EXCLUDES anything billed under
     *  conversation-based pricing (CBP) — those are one charge per 24h
     *  window regardless of message count, so counting them here too would
     *  double-bill every follow-up message in the same window. See
     *  `billedConversations`. */
    billable: number;
    /** How many of `total` carry Meta's pricing confirmation. */
    metaConfirmed: number;
    /** Conversation-based-pricing (CBP) units billed in range — one per 24h
     *  conversation window, however many messages rode inside it. Disjoint
     *  from `billable` (PMP and CBP never double-count the same charge): a
     *  tenant/range that mixes both pricing models sums correctly as
     *  `billable + billedConversations`. Zero for an all-PMP tenant. */
    billedConversations: number;
    /** Billable-looking messages still at `sent` — Meta charges on delivery,
     *  so these are held out of `billable` until delivered. */
    pendingDelivery: number;
    /** Meta-confirmed `service` messages it charged for (past the free tier). */
    serviceBilled: number;
    /** Meta-confirmed `service` messages it did not charge for. */
    serviceFree: number;
    /** Meta-confirmed free, by `pricing.type`. */
    freeCustomerService: number;
    freeEntryPoint: number;
    freeOther: number;
    /** Messages with no Meta pricing yet — their category/billable is our estimate. */
    unconfirmed: number;
  };
  bySource: { campaign: number; automation: number; bot: number; manual: number };
  /** Ascending by date; days with no messages are omitted. */
  byDay: WhatsAppMessageDay[];
}

/** Result of "Verify with Meta": our delivered counts vs Meta's `pricing_analytics`
 *  over the same whole Meta days. `diff` is ours minus Meta's. */
export type WhatsAppUsageVerification =
  | {
      available: true;
      window: { start: string; end: string };
      /** Volumes only — the server strips Meta's cost, which is super-admin-only. */
      rows: Array<{ category: string; type: string; meta: number; ours: number; diff: number }>;
      totals: { meta: number; ours: number; diff: number };
      /** Delivered messages here that carry no Meta pricing on our side. */
      ourUnconfirmed: number;
      matches: boolean;
    }
  | { available: false; reason: string; message: string };

export interface UsagePeriodRecord {
  _id: string;
  periodStart: string;
  periodEnd: string;
  planTypeAtClose: 'free' | 'pro' | 'enterprise';
  messagesLimitAtClose: number;
  apiCallsLimitAtClose: number;
  messagesUsed: number;
  apiCallsUsed: number;
  aiTokensUsed: number;
  aiCostUsd: number;
  aiRequestCount: number;
  isLegacySnapshot?: boolean;
  finalizedAt: string;
}

export interface BillingTransactionRecord {
  _id: string;
  planName: 'free' | 'basic' | 'professional' | 'enterprise';
  billingCycle: 'monthly' | 'yearly';
  amount: number;
  currency: string;
  status: 'created' | 'pending' | 'authorized' | 'captured' | 'failed' | 'expired' | 'refunded';
  gateway: 'razorpay' | 'cashfree' | 'manual';
  chargeType: 'one_time' | 'subscription_authorization' | 'subscription_recurring';
  /** How this transaction actually reached its terminal status — absent on
   *  rows still pending, or predating this field. */
  resolvedVia?: 'webhook' | 'redirect' | 'reconciliation';
  paidAt?: string;
  failureReason?: string;
  createdAt: string;
}

interface CreateCheckoutOrderRequest {
  /** Authoritative selector — never match a plan by name (Stripe price_xxx /
   *  Razorpay plan_xxx convention). planName is accepted server-side only as
   *  a fallback for callers that don't have the id in hand. */
  planId?: string;
  planName?: string;
  billingCycle: 'monthly' | 'yearly';
  /** Only sent when the paying admin has no phoneNumber on file yet (see
   *  PHONE_NUMBER_REQUIRED handling in BillingActivePlan.tsx) — the backend
   *  persists it onto the user record so it isn't asked for again. */
  phoneNumber?: string;
}

interface CreateCheckoutOrderResponse {
  transactionId: string;
  gatewayOrderId: string;
  paymentSessionId?: string;
  amount: number;
  currency: string;
  planName: string;
  billingCycle: 'monthly' | 'yearly';
  gateway: string;
  tenantName: string;
}

interface ConfirmCheckoutOrderResponse {
  skipped: boolean;
  reason?: string;
  transaction?: {
    transactionId: string;
    gatewayOrderId: string;
    status: string;
    planName: string;
    billingCycle: string;
    amount: number;
    currency: string;
    failureReason?: string;
  };
}

export type AlertType = 'critical' | 'warning' | 'info';
export type AlertCategory =
  | 'configuration'
  | 'ai_pipeline'
  | 'webhook'
  | 'delivery'
  | 'billing'
  | 'routing'
  | 'audit';

export interface SystemAlert {
  _id: string;
  tenantId: string;
  type: AlertType;
  category: AlertCategory;
  code: string;
  title: string;
  message: string;
  trigger: { mechanism: string; action: string; step: string };
  context: Record<string, unknown>;
  acknowledged: boolean;
  acknowledgedAt?: string;
  firstOccurredAt: string;
  lastOccurredAt: string;
  occurrenceCount: number;
  resolved: boolean;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type ActivitySection = 'enquiries' | 'webCaptures' | 'instagramComments' | 'appointments';
/** Per-section "new since you last looked" counts. A missing key = no access to that section. */
export type ActivityBadges = Partial<Record<ActivitySection, number>>;

export interface AlertSummary {
  total: number;
  /** Open alerts nobody has acknowledged yet — drives the sidebar badge. */
  unacknowledged: number;
  critical: number;
  warning: number;
  info: number;
}

export type ExternalApiClientMode = 'live' | 'test';
export type ExternalApiClientStatus = 'active' | 'revoked' | 'suspended';
export type ExternalApiScope =
  | 'send_template'
  | 'read_status'
  | 'list_templates'
  | 'trigger_campaign'
  | 'manage_contacts'
  | 'manage_leads'
  | 'manage_appointments';

/**
 * Record-change callback events an agent can opt in to. Distinct from the three legacy
 * ack events (message.status_update / campaign.progress / campaign.completed), which every
 * agent with a callback URL receives and which are not configurable here.
 */
export type RecordCallbackEventType =
  | 'lead.created'
  | 'lead.reengaged'
  | 'lead.updated'
  | 'lead.deleted'
  | 'appointment.created'
  | 'appointment.updated'
  | 'appointment.deleted';

export interface RecordSubscription {
  event: RecordCallbackEventType;
  /** 'own' = only records this agent pushed or was told about; 'all' = every record in the tenant. */
  scope: 'own' | 'all';
  /** *.updated only. Empty/omitted = any meaningful field change. */
  fields?: string[];
}

export interface ExternalApiClient {
  _id: string;
  tenantId: string;
  name: string;
  label?: string;
  mode: ExternalApiClientMode;
  apiKeyId: string;
  scopes: ExternalApiScope[];
  status: ExternalApiClientStatus;
  callbackUrl?: string;
  rateLimitPerMinute?: number;
  burstAllowance?: number;
  allowedTemplateNames?: string[];
  allowedCampaignIds?: string[];
  ipAllowlist?: string[];
  lastUsedAt?: string;
  /** Opt-in record-change events. Absent/empty = this agent receives none of them. */
  recordSubscriptions?: RecordSubscription[];
  /** Opt-in loop guard: when true we do NOT send this CRM changes it made itself. Default off —
   *  events are delivered regardless of origin and `changeSource` says who caused them. */
  suppressSelfCausedEvents?: boolean;
  /** Set only for test-mode keys (1h after create/rotate); live keys never expire. */
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExternalApiClientInput {
  name: string;
  label?: string;
  mode?: ExternalApiClientMode;
  scopes: ExternalApiScope[];
  callbackUrl?: string;
  rateLimitPerMinute?: number;
  burstAllowance?: number;
}

export interface ExternalApiClientSecretReveal {
  client: ExternalApiClient;
  apiKey: string;
  callbackSigningSecret?: string;
}

class AdminApiService {
  // Dashboard
  /** Tenant home overview — see Dashboard.tsx. */
  async getDashboardOverview(): Promise<DashboardOverview> {
    const response = await api.get<{ success: boolean; data: DashboardOverview }>(
      '/admin/dashboard/overview'
    );
    return response.data;
  }

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const response = await api.get<{ success: boolean; data: DashboardMetrics }>(
      '/admin/dashboard/metrics'
    );
    return response.data; // response is { success: true, data: {...} }, so response.data is the metrics
  }

  // Conversations
  async getConversations(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    /** Only contacts with an open 24h WhatsApp customer-service window right now. */
    windowActive?: boolean;
    /** Filter by channel — omit for all channels. */
    channel?: 'whatsapp' | 'instagram';
    tags?: string;
    /** WhatsApp-native advanced filter key (see contact-filter.service.ts). */
    advanced?: string;
    /** Meta provider error code — only used when advanced === 'failedByError'. */
    errorCode?: number;
    /** Campaign to scope the check to — only used when advanced === 'neverDeliveredThisCampaign'. */
    campaignId?: string;
    /** Tier 2 segment filters (see contact-segment-filter.service.ts). */
    contactSource?: string;
    dateAddedFrom?: string;
    dateAddedTo?: string;
    engagementTier?: string;
    frequencyMin?: number;
    frequencyMax?: number;
    /** JSON-stringified Array<{key, value}> — combines with `tags` under `matchMode`. */
    attributes?: string;
    matchMode?: 'all' | 'any';
    campaignSentFrom?: string;
    campaignSentTo?: string;
    /** Tier 3 (see contact-segment-filter.service.ts). 'unassigned' or a team-member userId. */
    assignedTo?: string;
    /** 0–100 (percent). Set => quality-risk filter is active. */
    minFailureRate?: number;
    minAttempts?: number;
    /** 'no' = never messaged AND no notes; 'yes' = the inverse. */
    contacted?: 'yes' | 'no';
    /** 'yes' = has at least one note; 'no' = none. Independent of `contacted`. */
    notes?: 'yes' | 'no';
    /** Filter by routing state — all four values are exact statuses:
     *  'needs_human' = 'pending_human' (escalated, still unclaimed). Once a
     *  conversation is claimed it moves to 'human', not 'needs_human'. */
    routingState?: 'bot' | 'pending_human' | 'human' | 'needs_human';
    /** List sort — omit (or 'recent') for most-recent-activity-of-either-direction
     *  (the default inbox order); 'incoming' for the customer-reply-priority order
     *  that's immune to bulk outbound sends (campaigns/automation) burying an
     *  engaged contact. */
    sort?: 'recent' | 'incoming';
    /** Only chats an automation's Flag node marked for review. Omit for the
     *  normal unfiltered list — there is deliberately no `false` (see the
     *  partial index note in Conversation.model.ts). */
    flagged?: true;
  }, requestConfig?: RequestOptions): Promise<{ data: Conversation[]; meta: any }> {
    const response = await api.get<{ success: boolean; data: Conversation[]; meta: any }>(
      '/admin/conversations',
      { params, ...requestConfig }
    );
    return { data: response.data, meta: response.meta };
  }

  /**
   * Instagram inbox list — separate store (InstagramConversation collection),
   * rows shaped identically to getConversations' rows so the ChatLogs UI can
   * merge the two channels client-side for its "All channels" view.
   */
  async getInstagramConversations(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
      windowActive?: boolean;
      tags?: string;
      assignedTo?: string;
      /** Thread `createdAt` bounds — ISO instants, not bare 'YYYY-MM-DD' (see
       *  ChatLogs' resolveDateAddedRange). Same names and semantics as
       *  getConversations' Tier 2 pair, so one date range covers both channels. */
      dateAddedFrom?: string;
      dateAddedTo?: string;
    },
    requestConfig?: RequestOptions
  ): Promise<{ data: Conversation[]; meta: any }> {
    const response = await api.get<{ success: boolean; data: Conversation[]; meta: any }>(
      '/admin/instagram/conversations',
      { params, ...requestConfig }
    );
    return { data: response.data, meta: response.meta };
  }

  /** Instagram thread detail — same cursor contract as getConversationDetail. */
  async getInstagramConversationDetail(
    id: string,
    params?: { limit?: number; beforeMessageId?: string; beforeCreatedAt?: string }
  ): Promise<Conversation & { hasMoreMessages?: boolean }> {
    const response = await api.get<{
      success: boolean;
      data: Conversation & { hasMoreMessages?: boolean };
    }>(`/admin/instagram/conversations/${id}`, { params });
    return response.data;
  }

  async markInstagramConversationRead(id: string, requestConfig?: RequestOptions): Promise<void> {
    await api.put(`/admin/instagram/conversations/${id}/read`, undefined, requestConfig);
  }

  /** Instagram analogue of assignConversation — same body shape, its own store. */
  async assignInstagramConversation(conversationId: string, userId: string): Promise<void> {
    await api.put(`/admin/instagram/conversations/${conversationId}/assign`, { assignedTo: userId });
  }

  /** Self-assign an unclaimed (or already-mine) Instagram thread. */
  async claimInstagramConversation(conversationId: string): Promise<void> {
    await api.post(`/admin/instagram/conversations/${conversationId}/claim`, {});
  }

  /** Clears the assignment — Instagram has no bot yet, so there is no
   *  hand-back-to-bot equivalent, only a plain unassign. */
  async unassignInstagramConversation(conversationId: string): Promise<void> {
    await api.post(`/admin/instagram/conversations/${conversationId}/unassign`, {});
  }

  /** User ids an Instagram thread may be assigned to (Full on the 'instagram'
   *  module) — the same pool the backend's reassign accepts. */
  async getInstagramAssignableUserIds(): Promise<string[]> {
    const response = await api.get<{ success: boolean; data: { users: Array<{ _id: string }> } }>(
      '/admin/instagram/assignable-users'
    );
    return (response.data?.users || []).map((u) => u._id);
  }

  /** Reversible archive/reactivate for an Instagram thread — same contract as setContactStatus. */
  async setInstagramConversationStatus(
    id: string,
    status: 'active' | 'archived'
  ): Promise<{ conversation: Conversation }> {
    const response = await api.patch<{
      success: boolean;
      data: { conversation: Conversation };
    }>(`/admin/instagram/conversations/${id}/status`, { status });
    return response.data;
  }

  /** Replace an Instagram thread's tags wholesale — same contract as updateContact({tags}). */
  async setInstagramConversationTags(
    id: string,
    tags: string[]
  ): Promise<{ conversation: Conversation }> {
    const response = await api.put<{
      success: boolean;
      data: { conversation: Conversation };
    }>(`/admin/instagram/conversations/${id}/tags`, { tags });
    return response.data;
  }

  // ── Instagram conversation notes (inbox side panel) ─────────────────────────
  async getInstagramConversationNotes(
    conversationId: string,
    requestConfig?: RequestOptions
  ): Promise<ContactNote[]> {
    const response = await api.get<{ success: boolean; data: { notes: ContactNote[] } }>(
      `/admin/instagram/conversations/${conversationId}/notes`,
      requestConfig
    );
    return response.data?.notes || [];
  }

  async addInstagramConversationNote(conversationId: string, text: string): Promise<ContactNote> {
    const response = await api.post<{ success: boolean; data: { note: ContactNote } }>(
      `/admin/instagram/conversations/${conversationId}/notes`,
      { text }
    );
    return response.data.note;
  }

  async deleteInstagramConversationNote(conversationId: string, noteId: string): Promise<void> {
    await api.delete(`/admin/instagram/conversations/${conversationId}/notes/${noteId}`);
  }

  /** WhatsApp-store equivalent — used when an inbound lands while the thread is open. */
  async markConversationRead(id: string, requestConfig?: RequestOptions): Promise<void> {
    await api.put(`/admin/conversations/${id}/read`, undefined, requestConfig);
  }

  /** Sidebar badge source — count of conversations with at least one unread inbound message. */
  async getConversationsUnreadSummary(requestConfig?: RequestOptions): Promise<{
    total: number;
    byChannel: { whatsapp: number; instagram: number };
    assignedToMe: number;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        total: number;
        byChannel: { whatsapp: number; instagram: number };
        assignedToMe: number;
      };
    }>('/admin/conversations/unread-summary', requestConfig);
    return response.data;
  }

  // Saved dynamic segments (Tier 3)
  async getSegments(requestConfig?: RequestOptions): Promise<Segment[]> {
    const response = await api.get<{ success: boolean; data: Segment[] }>(
      '/admin/segments',
      requestConfig
    );
    return response.data;
  }

  async createSegment(payload: {
    name: string;
    description?: string;
    filterParams: SegmentFilterParams;
  }): Promise<Segment> {
    const response = await api.post<{ success: boolean; data: Segment }>('/admin/segments', payload);
    return response.data;
  }

  async updateSegment(
    id: string,
    payload: { name?: string; description?: string; filterParams?: SegmentFilterParams }
  ): Promise<Segment> {
    const response = await api.patch<{ success: boolean; data: Segment }>(
      `/admin/segments/${id}`,
      payload
    );
    return response.data;
  }

  async deleteSegment(id: string): Promise<{ deleted: boolean }> {
    const response = await api.delete<{ success: boolean; data: { deleted: boolean } }>(
      `/admin/segments/${id}`
    );
    return response.data;
  }

  /**
   * Touch-state split for the current contact view. Takes the same filter
   * params as getConversations so the counts always describe the rows on
   * screen. Envelope-tolerant and zero-filled — a missing/older backend must
   * degrade to blank chips, never break the page.
   */
  async getContactTouchSummary(
    params?: Record<string, unknown>,
    requestConfig?: RequestOptions
  ): Promise<{
    total: number;
    contacted: number;
    notContacted: number;
    withNotes: number;
    messaged: number;
  }> {
    const response = await api.get<any>('/admin/conversations/touch-summary', {
      params,
      ...requestConfig,
    });
    const d = response?.data ?? response ?? {};
    return {
      total: Number(d.total) || 0,
      contacted: Number(d.contacted) || 0,
      notContacted: Number(d.notContacted) || 0,
      withNotes: Number(d.withNotes) || 0,
      messaged: Number(d.messaged) || 0,
    };
  }

  /** Distinct tags across all contacts/conversations, for the contacts list filter and assignment rules. */
  async getContactTags(requestConfig?: RequestOptions): Promise<string[]> {
    const response = await api.get<{ success: boolean; tags?: string[]; data?: { tags?: string[] } }>(
      '/admin/conversations/tags',
      requestConfig
    );
    // Tolerate both {tags} and {data:{tags}} envelopes — a missing/older backend
    // must degrade to an empty vocabulary, never propagate undefined into
    // Autocomplete options (crashes the whole Contacts page).
    const tags = response?.tags ?? response?.data?.tags;
    return Array.isArray(tags) ? tags : [];
  }

  // Contacts (campaign-targetable; create/import without a real inbound message)
  async createContact(payload: {
    name?: string;
    phone: string;
    email?: string;
    countryCode?: string;
    tags?: string[];
    customAttributes?: Array<{ key: string; value: string | number | boolean }>;
  }): Promise<{ contact: Conversation; created: boolean }> {
    const response = await api.post<{
      success: boolean;
      data: { contact: Conversation; created: boolean };
    }>('/admin/contacts', payload);
    return response.data;
  }

  /**
   * One contact's full CRM record for the contact detail page. Returns the
   * same row shape the list serves (shared serializer server-side) plus notes,
   * campaign engagement and opt-out. 404s for an id outside this tenant.
   */
  async getContact(id: string, requestConfig?: RequestOptions): Promise<ContactDetail> {
    const response = await api.get<{ success: boolean; data: { contact: ContactDetail } }>(
      `/admin/contacts/${id}`,
      requestConfig
    );
    return response.data.contact;
  }

  /**
   * Merged notes + audit-trail timeline for one contact — its own events plus
   * those of any lead sharing its phone number. `limit` is clamped to 200
   * server-side.
   */
  async getContactActivity(
    id: string,
    params: { limit?: number; includeRouting?: boolean } = {},
    requestConfig?: RequestOptions
  ): Promise<{ items: ContactActivityItem[]; hasMore: boolean }> {
    const { includeRouting, ...rest } = params;
    const response = await api.get<{
      success: boolean;
      data: { items: ContactActivityItem[]; hasMore: boolean };
    }>(`/admin/contacts/${id}/activity`, {
      params: { ...rest, ...(includeRouting === false ? { routing: 0 } : {}) },
      ...requestConfig,
    });
    return { items: response.data?.items || [], hasMore: Boolean(response.data?.hasMore) };
  }

  /**
   * Appointments list. Typed wrapper over the same GET /admin/appointments the
   * Appointments page calls through raw apiService — added here (rather than
   * reworking that page) for the contact detail card, which needs the `phone`
   * filter. Responds flat (`{success, items, total}`), no {data} wrapper.
   */
  async getAppointments(
    params: { phone?: string; limit?: number; skip?: number } = {},
    requestConfig?: RequestOptions
  ): Promise<{ items: AppointmentSummary[]; total: number }> {
    const response = await api.get<{ success: boolean; items: AppointmentSummary[]; total: number }>(
      '/admin/appointments',
      { params, ...requestConfig }
    );
    return { items: response.items || [], total: response.total ?? 0 };
  }

  /** Full-form edit of an existing contact, including its custom attributes. */
  async updateContact(
    id: string,
    payload: {
      name?: string;
      phone?: string;
      email?: string;
      countryCode?: string;
      tags?: string[];
      customAttributes?: Array<{ key: string; value: string | number | boolean }>;
    }
  ): Promise<{ contact: Conversation; relinked?: { leads: number; appointments: number } }> {
    const response = await api.put<{
      success: boolean;
      data: { contact: Conversation; relinked?: { leads: number; appointments: number } };
    }>(`/admin/contacts/${id}`, payload);
    return response.data;
  }

  /**
   * Advisory "can this contact use this number?" — format + workspace-wide uniqueness. POST with a
   * body so the number never sits in a URL. `owner` is present only when the caller may see who
   * holds it. The save re-checks; this exists so the field can say so before the user presses save.
   */
  async checkContactPhone(
    phone: string,
    excludeContactId?: string
  ): Promise<{
    valid: boolean;
    available: boolean;
    normalized?: string;
    owner?: { contactId: string; contactName?: string };
  }> {
    const response = await api.post<{
      success: boolean;
      data: {
        valid: boolean;
        available: boolean;
        normalized?: string;
        owner?: { contactId: string; contactName?: string };
      };
    }>('/admin/contacts/phone-check', {
      phone,
      ...(excludeContactId ? { excludeId: excludeContactId } : {}),
    });
    return response.data;
  }

  // ── Contact notes (inbox side panel) ──────────────────────────────────────
  async getContactNotes(contactId: string, requestConfig?: RequestOptions): Promise<ContactNote[]> {
    const response = await api.get<{ success: boolean; data: { notes: ContactNote[] } }>(
      `/admin/contacts/${contactId}/notes`,
      requestConfig
    );
    return response.data?.notes || [];
  }

  async addContactNote(contactId: string, text: string): Promise<ContactNote> {
    const response = await api.post<{ success: boolean; data: { note: ContactNote } }>(
      `/admin/contacts/${contactId}/notes`,
      { text }
    );
    return response.data.note;
  }

  async deleteContactNote(contactId: string, noteId: string): Promise<void> {
    await api.delete(`/admin/contacts/${contactId}/notes/${noteId}`);
  }

  /** Reversible deactivate ('archived') / reactivate ('active') toggle. */
  async setContactStatus(
    id: string,
    status: 'active' | 'archived'
  ): Promise<{ contact: Conversation }> {
    const response = await api.patch<{
      success: boolean;
      data: { contact: Conversation };
    }>(`/admin/contacts/${id}/status`, { status });
    return response.data;
  }

  /** Permanently removes the contact and its message history. Irreversible. */
  async deleteContact(id: string): Promise<void> {
    await api.delete<{ success: boolean }>(`/admin/contacts/${id}`);
  }

  /**
   * Bulk-delete contacts by explicit ids OR by the same search/status/tags filter the
   * contacts list itself uses — "select all matching the current view". Resolves to a
   * single DB trip on the backend regardless of how many contacts match.
   */
  async bulkDeleteContacts(payload: ContactBulkSelection): Promise<{ deletedCount: number }> {
    const response = await api.delete<{ success: boolean; data: { deletedCount: number } }>(
      '/admin/contacts/bulk',
      { data: payload }
    );
    return response.data;
  }

  /**
   * Owner/admin-only: contact audit trail — creates, updates, archives,
   * deletes, plus the one-row-per-operation summaries for every bulk action
   * (which carry no resourceId and so never show on an individual contact's
   * timeline).
   */
  async getContactAuditLog(params: {
    page?: number;
    limit?: number;
    action?: 'created' | 'updated' | 'archived' | 'deleted';
    bulkOnly?: boolean;
  }): Promise<{
    events: ContactAuditEvent[];
    total: number;
    page: number;
    limit: number;
  }> {
    const response = await api.get<{
      success: boolean;
      data: { events: ContactAuditEvent[]; total: number; page: number; limit: number };
    }>('/admin/contacts/audit-log', { params });
    return response.data;
  }

  /**
   * Add or remove tags across a hand-picked id list OR everything matching the
   * current view's filter. One `updateMany` on the backend regardless of size.
   *
   * `tagValues` is the payload (which tags to apply); `tags` inside the filter
   * half is the SELECTION (which contacts) — two different things.
   * Filter mode is owner/admin-only and requires `confirmed: true`.
   */
  async bulkUpdateContactTags(
    payload: ContactBulkSelection,
    operation: 'add' | 'remove',
    tagValues: string[]
  ): Promise<{ matchedCount: number; modifiedCount: number }> {
    const response = await api.post<{
      success: boolean;
      data: { matchedCount: number; modifiedCount: number };
    }>('/admin/contacts/bulk/tags', { ...payload, operation, tagValues });
    return response.data;
  }

  /** Bulk-set status on contacts by explicit ids OR by search/status/tags filter. Single DB trip on the backend. */
  async bulkSetContactsStatus(
    payload: ContactBulkSelection,
    targetStatus: 'active' | 'archived'
  ): Promise<{ modifiedCount: number }> {
    const response = await api.patch<{ success: boolean; data: { modifiedCount: number } }>(
      '/admin/contacts/bulk/status',
      { ...payload, targetStatus }
    );
    return response.data;
  }

  /**
   * Owner-only: reassign every contact matching a filter (never an explicit id
   * list — that's `bulkAssignContacts` above) to one team member, overwriting
   * whoever currently owns them. Requires `confirmed: true` since it can touch
   * every contact matching the current view, including ones already assigned
   * to someone else.
   */
  async bulkAssignContactsByFilter(
    filter: { tags?: string[]; status?: string; search?: string; matchMode?: 'all' | 'any' },
    userId: string
  ): Promise<{ assigned: number }> {
    const response = await api.post<{ success: boolean; data: { assigned: number } }>(
      '/admin/contacts/bulk/assign',
      { ...filter, userId, confirmed: true }
    );
    return response.data;
  }

  /** Owner-only: unassign every contact matching a filter that currently has an owner. */
  async bulkUnassignContactsByFilter(
    filter: { tags?: string[]; status?: string; search?: string; matchMode?: 'all' | 'any' },
    applyRules?: boolean
  ): Promise<{ unassigned: number; rulesApplied: number }> {
    const response = await api.post<{
      success: boolean;
      data: { unassigned: number; rulesApplied: number };
    }>('/admin/contacts/bulk/unassign', { ...filter, applyRules, confirmed: true });
    return response.data;
  }

  /** Distinct custom-attribute keys already in use, for the Custom Fields UI's typeahead. */
  async getContactAttributeKeys(requestConfig?: RequestOptions): Promise<string[]> {
    const response = await api.get<{ success: boolean; data: { keys: string[] } }>(
      '/admin/contacts/attribute-keys',
      requestConfig
    );
    return response.data.keys;
  }

  async importContacts(
    rows: Record<string, string>[],
    fieldMapping: Record<string, string>,
    tags?: string[],
    options?: {
      dryRun?: boolean;
      overwriteExisting?: boolean;
      defaultCountry?: string;
      assignment?: ContactImportAssignment;
    }
  ): Promise<ContactImportResult> {
    const response = await api.post<{
      success: boolean;
      data: ContactImportResult;
    }>(
      '/admin/contacts/import',
      { rows, fieldMapping, tags, ...options },
      { timeout: 120000 } // bulk imports of thousands of rows can run past the 30s default
    );
    return response.data;
  }

  /**
   * `messages` in the response is a page, not the full history — only the
   * most recent `limit` (default 40) messages, or the `limit` immediately
   * before `beforeMessageId`/`beforeCreatedAt` when paging further back.
   * `beforeMessageId`/`beforeCreatedAt` must be passed together (the oldest
   * currently-loaded message's `_id`/`createdAt`) — omit both for the first page.
   */
  async getConversationDetail(
    id: string,
    params?: { limit?: number; beforeMessageId?: string; beforeCreatedAt?: string }
  ): Promise<Conversation & { hasMoreMessages?: boolean }> {
    const response = await api.get<{ success: boolean; data: Conversation & { hasMoreMessages?: boolean } }>(
      `/admin/conversations/${id}`,
      { params }
    );
    return response.data;
  }

  async assignConversation(conversationId: string, userId: string): Promise<void> {
    await api.put(`/admin/conversations/${conversationId}/assign`, { assignedTo: userId });
  }

  /** Self-assign an unassigned or own conversation — available to any tenant_admin. */
  async claimConversation(conversationId: string): Promise<void> {
    await api.post(`/admin/conversations/${conversationId}/claim`, {});
  }

  /** Return a conversation to the bot — clears assignment and escalation record.
   *  Only the current assignee or the tenant_owner may do this. */
  async handBackToBot(conversationId: string): Promise<void> {
    await api.post(`/admin/conversations/${conversationId}/handback-to-bot`, {});
  }

  /** Toggle automated bot replies on or off for a conversation.
   *  action='stop'  → bot pauses, ownership unchanged (human takes over).
   *  action='resume' → bot resumes, assignment is kept.
   *  PUT /admin/conversations/:id/bot-state */
  async setConversationBotState(
    conversationId: string,
    action: 'stop' | 'resume'
  ): Promise<void> {
    await api.put(`/admin/conversations/${conversationId}/bot-state`, { action });
  }

  /** Clear the "flagged for review" mark set by an automation's Flag node.
   *  Idempotent — safe to call on a chat someone else just cleared. */
  async clearConversationFlag(conversationId: string): Promise<void> {
    await api.post(`/admin/conversations/${conversationId}/clear-flag`, {});
  }

  /** Reports module — server-streamed CSV export of a data set. */
  async exportReport(
    type: 'leads' | 'conversations' | 'campaigns' | 'appointments' | 'handoffs' | 'ai-chat',
    params: Record<string, string>
  ): Promise<Blob> {
    const response = await api.get(`/admin/reports/${type}/export`, {
      params,
      responseType: 'blob',
    });
    return response as any;
  }

  async exportConversation(conversationId: string, channel?: 'whatsapp' | 'instagram'): Promise<Blob> {
    const response = await api.get(`/admin/conversations/${conversationId}/export`, {
      responseType: 'blob',
      ...(channel ? { params: { channel } } : {}),
    });
    return response as any;
  }

  // LLM Configuration
  async getLLMConfig(requestConfig?: RequestOptions): Promise<LLMConfig> {
    const response = await api.get<{ success: boolean; data: LLMConfig }>(
      '/admin/llm-config',
      requestConfig
    );
    return response.data;
  }

  // Read-only model catalog for the agent editor's Model dropdown — deliberately
  // separate from getLLMConfig so it keeps working even when the LLM
  // Configuration module is disabled for this tenant (see the matching backend
  // route comment in admin.routes.ts). Returns id/isEnabled only, no apiKey.
  async getLLMConfigModels(
    requestConfig?: RequestOptions
  ): Promise<Array<{ id: string; isEnabled: boolean }>> {
    const response = await api.get<{
      success: boolean;
      data: { availableModels: Array<{ id: string; isEnabled: boolean }> };
    }>('/admin/llm-config/models', requestConfig);
    return response.data.availableModels;
  }

  async updateLLMConfig(
    config: Partial<Pick<LLMConfig, 'apiKey' | 'temperature' | 'maxTokens'>>
  ): Promise<void> {
    await api.put('/admin/llm-config', config);
  }

  async addLLMModel(modelId: string): Promise<LLMModelEntry[]> {
    const response = await api.post<{
      success: boolean;
      data: { availableModels: LLMModelEntry[] };
    }>('/admin/llm-config/models', { modelId });
    return response.data.availableModels;
  }

  async removeLLMModel(modelId: string): Promise<LLMModelEntry[]> {
    const response = await api.delete<{
      success: boolean;
      data: { availableModels: LLMModelEntry[] };
    }>(`/admin/llm-config/models/${encodeURIComponent(modelId)}`);
    return response.data.availableModels;
  }

  // Automation pause — tenant's own runtime kill switch for scheduled/behavior
  // campaigns and automation workflows (independent of plan entitlement).
  async getAutomationSettings(): Promise<{ paused: boolean }> {
    const response = await api.get<{ success: boolean; data: { paused: boolean } }>(
      '/admin/automation-settings'
    );
    return response.data;
  }

  async updateAutomationSettings(paused: boolean): Promise<{ paused: boolean }> {
    const response = await api.patch<{ success: boolean; data: { paused: boolean } }>(
      '/admin/automation-settings',
      { paused }
    );
    return response.data;
  }

  // Bot→human escalation config (Tenant.settings.routing.escalation) — the phrases
  // that hand a WhatsApp conversation to a team member. Read on every inbound
  // message; see backend escalation-keyword-matcher.util.ts.
  async getRoutingSettings(requestConfig?: RequestOptions): Promise<RoutingSettingsResponse> {
    const response = await api.get<{ success: boolean; data: RoutingSettingsResponse }>(
      '/admin/routing-settings',
      requestConfig
    );
    return response.data;
  }

  /** PATCH — only the fields passed are written, so a caller can save one control
   *  without round-tripping (and risking clobbering) the rest of the config. */
  async updateRoutingSettings(
    payload: Partial<EscalationSettings>
  ): Promise<{ message: string; escalation: EscalationSettings }> {
    const response = await api.patch<{
      success: boolean;
      data: { message: string; escalation: EscalationSettings };
    }>('/admin/routing-settings', payload);
    return response.data;
  }

  /** Dry-run a message against the saved config. Runs the same evaluator the webhook
   *  does, so it can't disagree with real behaviour. Reads only — nothing is sent. */
  async testRoutingPhrase(message: string): Promise<RoutingPhraseTestResult> {
    const response = await api.post<{ success: boolean; data: RoutingPhraseTestResult }>(
      '/admin/routing-settings/test',
      { message }
    );
    return response.data;
  }

  // RAG Vector (Pinecone) Configuration — tenant-owned, no global fallback
  async getRagVectorConfig(): Promise<RagVectorConfig> {
    const response = await api.get<{ success: boolean; data: RagVectorConfig }>(
      '/admin/rag/vector-config'
    );
    return response.data;
  }

  async saveRagVectorConfig(payload: {
    apiKey?: string | undefined;
    indexName: string;
    environment?: string | undefined;
  }): Promise<RagVectorConfig> {
    const response = await api.put<{ success: boolean; data: RagVectorConfig }>(
      '/admin/rag/vector-config',
      payload
    );
    return response.data;
  }

  // apiKey omitted/blank tests indexName against the currently saved key.
  async testRagVectorConnection(
    apiKey: string | undefined,
    indexName: string
  ): Promise<RagVectorTestResult> {
    const response = await api.post<{ success: boolean; data: RagVectorTestResult }>(
      '/admin/rag/vector-config/test-connection',
      { apiKey, indexName }
    );
    return response.data;
  }

  async backfillRagVectorAgents(): Promise<{ agentsProcessed: number; agentsFailed: number }> {
    // This re-ingests every agent sequentially (10-20s+ each against the RAG
    // microservice) — easily exceeds the global 30s default for tenants with
    // more than one or two agents, which would otherwise hide a real
    // success/failure result behind a client-side timeout.
    const response = await api.post<{
      success: boolean;
      data: { agentsProcessed: number; agentsFailed: number };
    }>('/admin/rag/vector-config/backfill', undefined, { timeout: 300000 });
    return response.data;
  }

  /** Update any subset of the RAG kill switches: `ragEnabled` (WhatsApp) and the
   *  per-channel flags. Only the fields passed are written (PATCH semantics). */
  async updateRagSettings(payload: {
    ragEnabled?: boolean;
    instagramRagEnabled?: boolean;
    playgroundRagEnabled?: boolean;
    widgetRagEnabled?: boolean;
  }): Promise<Record<string, boolean>> {
    const response = await api.patch<{ success: boolean; data: Record<string, boolean> }>(
      '/admin/rag/settings',
      payload
    );
    return response.data;
  }

  /** AI usage for a time frame: omit the dates for the current billing period,
   *  or pass inclusive YYYY-MM-DD days (see BillingTimeframeSelect). */
  async getLLMUsage(params: { startDate?: string; endDate?: string } = {}): Promise<LLMUsageResponse> {
    const response = await api.get<{ success: boolean; data: LLMUsageResponse }>('/admin/llm-config/usage', {
      params,
    });
    return response.data;
  }

  // Team Management
  async getTeamMembers(requestConfig?: RequestOptions): Promise<TeamMember[]> {
    const response = await api.get<{ success: boolean; data: TeamMember[] }>(
      '/admin/team',
      requestConfig
    );
    return response.data;
  }

  /**
   * Assignee directory: id + name + email of active members. Unlike
   * getTeamMembers (userManagement-gated, full profiles), this works for ANY
   * authenticated member — use it for assignee name display and assign
   * pickers so scoped roles (no userManagement grant) still resolve names.
   */
  async getTeamDirectory(requestConfig?: RequestOptions): Promise<TeamMember[]> {
    const response = await api.get<{ success: boolean; data?: TeamMember[] }>(
      '/admin/team/directory',
      requestConfig
    );
    const rows = response?.data;
    return Array.isArray(rows) ? rows : [];
  }

  async createTeamMember(member: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: string;
  }): Promise<TeamMember> {
    const response = await api.post<{ success: boolean; data: { user: TeamMember } }>(
      '/admin/team',
      member
    );
    return response.data.user;
  }

  async updateTeamMember(id: string, updates: Partial<TeamMember>): Promise<void> {
    await api.put(`/admin/team/${id}`, updates);
  }

  async deleteTeamMember(id: string): Promise<void> {
    await api.delete(`/admin/team/${id}`);
  }

  // WhatsApp
  async sendMessage(to: string, message: string): Promise<void> {
    await api.post('/whatsapp/send', { to, message });
  }

  /** Manual template send — the only way back into a conversation once the
   *  24h window has closed. `variableValues` are positional literal values
   *  for the template body's `{{n}}` slots. `buttonUrlVariable` is the
   *  literal value for a dynamic URL button's `{{1}}` slot — required only if
   *  the template has one; omit entirely otherwise.
   *
   *  `headerMediaAssetId` swaps a library file into the template's
   *  IMAGE/VIDEO/DOCUMENT header for this one send — the only way to put a file
   *  in front of a customer once the 24h window has closed, since free-form
   *  media is undeliverable there. Omit it and the template sends with the file
   *  it was created with, exactly as before. The asset's type must match the
   *  header's; the server rejects a mismatch. */
  async sendWhatsAppTemplate(
    to: string,
    templateName: string,
    templateLanguage: string,
    variableValues: string[],
    buttonUrlVariable?: string,
    headerMediaAssetId?: string
  ): Promise<void> {
    await api.post('/whatsapp/send-template', {
      to,
      templateName,
      templateLanguage,
      variableValues,
      ...(buttonUrlVariable && { buttonUrlVariable }),
      ...(headerMediaAssetId && { headerMediaAssetId }),
    });
  }

  /** Manual media send — attaches a file from the tenant's media library to a
   *  live chat. Free-form media is deliverable only INSIDE the 24h window; the
   *  server rejects a closed window outright rather than recording a failed
   *  message, so the caller gets a readable error instead of a red bubble. */
  async sendWhatsAppMedia(to: string, mediaAssetId: string, caption?: string): Promise<void> {
    await api.post('/whatsapp/send-media', {
      to,
      mediaAssetId,
      ...(caption ? { caption } : {}),
    });
  }

  async getWhatsAppConfig(): Promise<WhatsAppConfig | null> {
    const response = await api.get<{ success: boolean; data: WhatsAppConfig | null }>(
      '/whatsapp/config'
    );
    return response.data;
  }

  async connectWhatsApp(config: WhatsAppConfig): Promise<void> {
    await api.post('/whatsapp/connect', config);
  }

  async updateWhatsAppConfig(config: Partial<WhatsAppConfig>): Promise<void> {
    await api.put('/whatsapp/config', config);
  }

  /** Split setup: part1 = Meta app ID, secret, webhook token; part2 = phone fields + access token */
  async updateWhatsAppConfigSection(
    section: 'part1' | 'part2',
    body: Partial<
      Pick<
        WhatsAppConfig,
        | 'phoneNumberId'
        | 'phoneNumber'
        | 'displayPhoneNumber'
        | 'wabaId'
        | 'accessToken'
        | 'metaAppId'
        | 'metaAppSecret'
        | 'webhookVerifyToken'
      >
    >
  ): Promise<string | undefined> {
    const res = await api.put<{ success: boolean; data: { message?: string } }>(
      '/whatsapp/config',
      {
        section,
        ...body,
      }
    );
    return res.data?.message;
  }

  /** On-demand pull of quality rating + messaging tier straight from Meta (bypasses the 6h cache). */
  async syncWhatsAppTierStatus(): Promise<WhatsAppTierStatus> {
    const response = await api.post<{ success: boolean; data: WhatsAppTierStatus }>(
      '/whatsapp/config/sync-tier'
    );
    return response.data;
  }

  async getInstagramConfig(requestConfig?: RequestOptions): Promise<InstagramConfig> {
    const response = await api.get<{ success: boolean; data: InstagramConfig }>(
      '/instagram/config',
      requestConfig
    );
    return response.data;
  }

  /** Returns the Instagram authorize URL — caller redirects the browser to it. */
  async connectInstagram(): Promise<{ authorizeUrl: string }> {
    const response = await api.post<{ success: boolean; data: { authorizeUrl: string } }>(
      '/instagram/connect'
    );
    return response.data;
  }

  async disconnectInstagram(): Promise<void> {
    await api.post('/instagram/disconnect');
  }

  /** Manual reply to an Instagram conversation — `to` is the contact's channelIdentity.value (igsid). */
  async sendInstagramMessage(to: string, message: string): Promise<{ messageId: string }> {
    const response = await api.post<{ success: boolean; data: { messageId: string } }>(
      '/instagram/send',
      { to, message }
    );
    return response.data;
  }

  /** Manual media send to an Instagram conversation — same shape/semantics as
   *  sendWhatsAppMedia (library file, 24h-window-gated), `to` is the igsid. */
  async sendInstagramMedia(to: string, mediaAssetId: string, caption?: string): Promise<void> {
    await api.post('/instagram/send-media', {
      to,
      mediaAssetId,
      ...(caption ? { caption } : {}),
    });
  }

  /** Typing indicator (Phase 2.4) — best-effort UX polish, never throws in a
   *  way the composer should react to (the endpoint itself always 200s; see
   *  instagram.controller.ts's sendTyping doc comment). `to` is the igsid. */
  async sendInstagramTyping(to: string, action: 'typing_on' | 'typing_off'): Promise<void> {
    await api.post('/instagram/typing', { to, action });
  }

  /** Phase 2.6 — reads straight from this tenant's own DB row (not a live
   *  Meta call), so this is always safe/cheap to call on page load. */
  async getInstagramIceBreakers(): Promise<InstagramIceBreaker[]> {
    const response = await api.get<{ success: boolean; data: { iceBreakers: InstagramIceBreaker[] } }>(
      '/instagram/ice-breakers'
    );
    return response.data?.iceBreakers || [];
  }

  /** An empty array DELETES the ice breakers from Meta (see the backend
   *  controller's own doc comment) rather than pushing an empty set. */
  async saveInstagramIceBreakers(iceBreakers: InstagramIceBreaker[]): Promise<InstagramIceBreaker[]> {
    const response = await api.put<{ success: boolean; data: { iceBreakers: InstagramIceBreaker[] } }>(
      '/instagram/ice-breakers',
      { iceBreakers }
    );
    return response.data?.iceBreakers || [];
  }

  async getInstagramPersistentMenu(): Promise<InstagramPersistentMenuItem[]> {
    const response = await api.get<{ success: boolean; data: { persistentMenu: InstagramPersistentMenuItem[] } }>(
      '/instagram/persistent-menu'
    );
    return response.data?.persistentMenu || [];
  }

  async saveInstagramPersistentMenu(
    persistentMenu: InstagramPersistentMenuItem[]
  ): Promise<InstagramPersistentMenuItem[]> {
    const response = await api.put<{ success: boolean; data: { persistentMenu: InstagramPersistentMenuItem[] } }>(
      '/instagram/persistent-menu',
      { persistentMenu }
    );
    return response.data?.persistentMenu || [];
  }

  // Instagram comments (Phase 3) — admin surface, under /admin/instagram/*
  async getInstagramComments(
    params: { page?: number; limit?: number; mediaId?: string } = {}
  ): Promise<{ comments: InstagramCommentRow[]; hasMore: boolean; page: number; limit: number }> {
    const response = await api.get<{
      success: boolean;
      data: { comments: InstagramCommentRow[]; hasMore: boolean; page: number; limit: number };
    }>('/admin/instagram/comments', { params });
    return response.data;
  }

  async getInstagramCommentRules(): Promise<InstagramCommentRule[]> {
    const response = await api.get<{ success: boolean; data: { rules: InstagramCommentRule[] } }>(
      '/admin/instagram/comment-rules'
    );
    return response.data?.rules || [];
  }

  async createInstagramCommentRule(input: InstagramCommentRuleInput): Promise<InstagramCommentRule> {
    const response = await api.post<{ success: boolean; data: { rule: InstagramCommentRule } }>(
      '/admin/instagram/comment-rules',
      input
    );
    return response.data.rule;
  }

  async updateInstagramCommentRule(
    id: string,
    patch: Partial<InstagramCommentRuleInput>
  ): Promise<InstagramCommentRule> {
    const response = await api.put<{ success: boolean; data: { rule: InstagramCommentRule } }>(
      `/admin/instagram/comment-rules/${id}`,
      patch
    );
    return response.data.rule;
  }

  async deleteInstagramCommentRule(id: string): Promise<void> {
    await api.delete(`/admin/instagram/comment-rules/${id}`);
  }

  // @mentions feed (Phase 3.3)
  async getInstagramMentions(
    params: { page?: number; limit?: number } = {}
  ): Promise<{ mentions: InstagramMentionRow[]; hasMore: boolean; page: number; limit: number }> {
    const response = await api.get<{
      success: boolean;
      data: { mentions: InstagramMentionRow[]; hasMore: boolean; page: number; limit: number };
    }>('/admin/instagram/mentions', { params });
    return response.data;
  }

  // Per-comment moderation (Phase 3.1)
  async setInstagramCommentHidden(commentId: string, hide: boolean): Promise<void> {
    await api.patch(`/admin/instagram/comments/${commentId}/hidden`, { hide });
  }

  async replyToInstagramComment(commentId: string, message: string): Promise<{ replyCommentId?: string }> {
    const response = await api.post<{ success: boolean; data: { replyCommentId?: string } }>(
      `/admin/instagram/comments/${commentId}/reply`,
      { message }
    );
    return response.data;
  }

  async deleteInstagramComment(commentId: string): Promise<void> {
    await api.delete(`/admin/instagram/comments/${commentId}`);
  }

  // Privacy Policy (self-service — gated by modules.privacyPolicy; the tab that
  // calls these is hidden when the module is off, see Settings.tsx's TAB_DEFS)
  async getPrivacyPolicy(): Promise<{ policy: PrivacyPolicyFields; publicUrl: string }> {
    const response = await api.get<{
      success: boolean;
      data: { policy: PrivacyPolicyFields; publicUrl: string };
    }>('/privacy-policy');
    return response.data;
  }

  async updatePrivacyPolicy(
    body: Partial<PrivacyPolicyFields>
  ): Promise<{ message: string; policy: PrivacyPolicyFields; publicUrl: string }> {
    const response = await api.put<{
      success: boolean;
      data: { message: string; policy: PrivacyPolicyFields; publicUrl: string };
    }>('/privacy-policy', body);
    return response.data;
  }

  // WhatsApp Message Templates
  async getWhatsAppTemplates(requestConfig?: RequestOptions): Promise<WhatsAppTemplate[]> {
    const response = await api.get<{ success: boolean; data: { templates: WhatsAppTemplate[] } }>(
      '/whatsapp/templates',
      requestConfig
    );
    return response.data?.templates || [];
  }

  /** Save (or overwrite) a local DRAFT template — no Meta submission. */
  async saveWhatsAppTemplateDraft(payload: {
    name: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    components: WhatsAppTemplateComponents;
    messageSendTtlSeconds?: number;
  }): Promise<WhatsAppTemplate> {
    const response = await api.post<{ success: boolean; data: { template: WhatsAppTemplate } }>(
      '/whatsapp/templates/drafts',
      payload
    );
    return response.data.template;
  }

  /**
   * Edit an existing (APPROVED/REJECTED/PAUSED) template IN PLACE — Meta re-reviews
   * it, keeping the same name + language. The previously-approved version keeps
   * sending until the edit is approved, so this never takes a live template offline.
   */
  async editWhatsAppTemplate(payload: {
    name: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    components: WhatsAppTemplateComponents;
    messageSendTtlSeconds?: number;
  }): Promise<WhatsAppTemplate> {
    const response = await api.put<{ success: boolean; data: { template: WhatsAppTemplate } }>(
      `/whatsapp/templates/${encodeURIComponent(payload.name)}/edit`,
      payload
    );
    return response.data.template;
  }

  /** Submit an existing DRAFT to Meta for review. */
  async submitWhatsAppTemplateDraft(name: string, language: string): Promise<WhatsAppTemplate> {
    const response = await api.post<{ success: boolean; data: { template: WhatsAppTemplate } }>(
      '/whatsapp/templates/drafts/submit',
      { name, language }
    );
    return response.data.template;
  }

  async createWhatsAppTemplate(payload: {
    name: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    components: WhatsAppTemplateComponents;
    /** AUTHENTICATION only — 30-900 seconds. Meta's 10-minute default applies if omitted. */
    messageSendTtlSeconds?: number;
  }): Promise<WhatsAppTemplate> {
    const response = await api.post<{ success: boolean; data: { template: WhatsAppTemplate } }>(
      '/whatsapp/templates',
      payload
    );
    return response.data.template;
  }

  async syncWhatsAppTemplates(): Promise<{ message?: string; templates: WhatsAppTemplate[] }> {
    const response = await api.post<{
      success: boolean;
      data: { message?: string; templates: WhatsAppTemplate[] };
    }>('/whatsapp/templates/sync');
    return response.data;
  }

  async uploadWhatsAppTemplateMedia(
    file: File
  ): Promise<{ handle: string; s3Key: string; mimeType: string; fileName: string }> {
    const response = await api.upload<{
      success: boolean;
      data: { handle: string; s3Key: string; mimeType: string; fileName: string };
    }>('/whatsapp/templates/media-upload', file);
    return response.data;
  }

  /**
   * Attach/replace the header media for an existing template: stores the
   * file privately in S3 and clears any cached Meta media id so the next
   * campaign send re-uploads it via Meta's Media API (`{ id: media_id }`).
   */
  async setWhatsAppTemplateHeaderMedia(
    name: string,
    language: string,
    file: File
  ): Promise<WhatsAppTemplate> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);

    const response = await api.put<{ success: boolean; data: { template: WhatsAppTemplate } }>(
      `/whatsapp/templates/${encodeURIComponent(name)}/header-media`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data.template;
  }

  /**
   * Re-upload the media for ONE carousel card (0-based index). Stores a fresh
   * private copy, clears that card's cached Meta media id, and clears the
   * template's media-unfit flag once every card has a stored source again. No
   * Meta re-approval — a same-type media swap is a send-time parameter.
   */
  async setWhatsAppTemplateCarouselMedia(
    name: string,
    language: string,
    cardIndex: number,
    file: File
  ): Promise<WhatsAppTemplate> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    formData.append('cardIndex', String(cardIndex));

    const response = await api.put<{ success: boolean; data: { template: WhatsAppTemplate } }>(
      `/whatsapp/templates/${encodeURIComponent(name)}/carousel-card-media`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data.template;
  }

  async deleteWhatsAppTemplate(name: string): Promise<void> {
    await api.delete(`/whatsapp/templates/${encodeURIComponent(name)}`);
  }

  /** Short-lived signed S3 URL to preview a template's uploaded header media (image/video/document). */
  async getWhatsAppTemplateHeaderMediaPreview(
    name: string,
    language: string,
    requestConfig?: RequestOptions
  ): Promise<{ previewUrl: string | null; mimeType?: string; fileName?: string }> {
    const response = await api.get<{
      success: boolean;
      data: { previewUrl: string | null; mimeType?: string; fileName?: string };
    }>(
      `/whatsapp/templates/${encodeURIComponent(name)}/header-media-preview?language=${encodeURIComponent(language)}`,
      requestConfig
    );
    return response.data;
  }

  /** Short-lived signed S3 URLs for every card of a CAROUSEL template's media, in one call. */
  async getWhatsAppTemplateCarouselMediaPreview(
    name: string,
    language: string,
    requestConfig?: RequestOptions
  ): Promise<Array<{ previewUrl: string | null; mimeType?: string; fileName?: string }>> {
    const response = await api.get<{
      success: boolean;
      data: { cards: Array<{ previewUrl: string | null; mimeType?: string; fileName?: string }> };
    }>(
      `/whatsapp/templates/${encodeURIComponent(name)}/carousel-media-preview?language=${encodeURIComponent(language)}`,
      requestConfig
    );
    return response.data.cards;
  }

  async updateWhatsAppTemplateHeaderMediaUrl(
    name: string,
    language: string,
    headerMediaUrl: string
  ): Promise<WhatsAppTemplate> {
    const response = await api.put<{ success: boolean; data: { template: WhatsAppTemplate } }>(
      `/whatsapp/templates/${encodeURIComponent(name)}/header-media-url`,
      { language, headerMediaUrl }
    );
    return response.data.template;
  }

  /**
   * Label what each `{{n}}` body variable (and the header's `{{1}}`, if any)
   * represents, e.g. "Customer name", "Order ID" — used to auto-suggest the
   * campaign variable mapping. Works for already-approved/synced templates.
   */
  async updateWhatsAppTemplateVariableLabels(
    name: string,
    language: string,
    labels: { variableLabels?: string[]; headerVariableLabel?: string }
  ): Promise<WhatsAppTemplate> {
    const response = await api.put<{ success: boolean; data: { template: WhatsAppTemplate } }>(
      `/whatsapp/templates/${encodeURIComponent(name)}/variable-labels`,
      { language, ...labels }
    );
    return response.data.template;
  }

  // Agents
  async getAgents(
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      topic?: string;
      purpose?: string;
      isActive?: boolean;
    },
    requestConfig?: RequestOptions
  ): Promise<{ data: any[]; meta: any }> {
    const response = await api.get('/admin/agents', { params, ...requestConfig });
    return { data: normalizeAgentPayload(response.data) || [], meta: response.meta };
  }

  async getAgentById(id: string): Promise<any> {
    const response = await api.get(`/admin/agents/${id}`);
    return normalizeAgentPayload(response.data);
  }

  async createAgent(agent: any): Promise<any> {
    const response = await api.post('/admin/agents', agent);
    return normalizeAgentPayload(response.data);
  }

  async updateAgent(id: string, agent: any): Promise<any> {
    const response = await api.put(`/admin/agents/${id}`, agent);
    return normalizeAgentPayload(response.data);
  }

  async deleteAgent(id: string): Promise<void> {
    await api.delete(`/admin/agents/${id}`);
  }

  /** Static starter templates (optionally vertical-scoped server-side) for the "New Agent" dialog. */
  async getAgentTemplates(requestConfig?: RequestOptions): Promise<any[]> {
    const response = await api.get<{ success: boolean; data: { templates: any[] } }>(
      '/admin/agents/templates',
      requestConfig
    );
    return response.data?.templates || [];
  }

  /**
   * Dry-run agent routing for a hypothetical customer message — which agent
   * would answer and why. Distinct from testRoutingPhrase above (that one
   * dry-runs the bot->human ESCALATION config, a different concept/endpoint
   * entirely, despite the similar name).
   */
  async testAgentRouting(message: string): Promise<any> {
    const response = await api.post<{ success: boolean; data: any }>('/admin/agents/routing-test', {
      message,
    });
    return response.data;
  }

  async getPlaygroundPrompt(agentId: string): Promise<any> {
    const response = await api.get(`/admin/playground/prompt?agentId=${agentId}`);
    return response.data;
  }

  async savePlaygroundPrompt(payload: {
    agentId?: string;
    agentName: string;
    systemPrompt: string;
  }): Promise<any> {
    const response = await api.post('/admin/playground/prompt', payload);
    return response.data;
  }

  async updateAgentTextKnowledgeBase(agentId: string, textContent: string): Promise<any> {
    const response = await api.put(`/admin/agents/${agentId}/knowledge-base/text`, {
      textContent,
    });
    return normalizeAgentPayload(response.data);
  }

  async getAgentStats(): Promise<any> {
    const response = await api.get('/admin/agents/stats');
    return response.data;
  }

  // Q&A Sources
  async getQASources(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ data: QASource[]; meta: any }> {
    const response = await api.get<{ success: boolean; data: QASource[]; meta: any }>('/admin/qa', {
      params,
    });
    return { data: response.data, meta: response.meta };
  }

  async createQASource(payload: {
    title: string;
    questions: string[];
    answer: string;
  }): Promise<QASource> {
    const response = await api.post<{ success: boolean; data: { qa: QASource } }>(
      '/admin/qa',
      payload
    );
    return response.data.qa;
  }

  async updateQASource(
    id: string,
    payload: { title: string; questions: string[]; answer: string }
  ): Promise<QASource> {
    const response = await api.put<{ success: boolean; data: { qa: QASource } }>(
      `/admin/qa/${id}`,
      payload
    );
    return response.data.qa;
  }

  async deleteQASource(id: string): Promise<void> {
    await api.delete(`/admin/qa/${id}`);
  }

  // File Sources
  async getFileSources(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ data: FileSource[]; meta: any }> {
    const response = await api.get<{ success: boolean; data: FileSource[]; meta: any }>(
      '/admin/files',
      { params }
    );
    return { data: response.data, meta: response.meta };
  }

  async uploadFileSource(file: File): Promise<FileSource> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<{ success: boolean; data: { file: FileSource } }>(
      '/admin/files',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.file;
  }

  async deleteFileSource(id: string): Promise<void> {
    await api.delete(`/admin/files/${id}`);
  }

  async getFileDownloadUrl(id: string): Promise<string> {
    const response = await api.get<{ success: boolean; data: { downloadUrl: string } }>(
      `/admin/files/${id}/download`
    );
    return response.data.downloadUrl;
  }

  // Media Assets (sendable-media library — files the bot delivers on request via a
  // send_media conversation-flow node). Distinct from FileSources (RAG knowledge).
  // Organised into nestable folders; assets carry a name + optional description.

  // ── Inbound conversation media (customer-sent WhatsApp attachments) ───────
  // Distinct from the media LIBRARY above: these are untrusted third-party
  // uploads gated on `activity.media`, not `sources.media`.

  /** Tenant-wide media browser. Cursor-paginated on `createdAt`. `channel`
   *  defaults server-side to 'whatsapp' when omitted — see
   *  conversation-media.controller.ts's own parseChannel helper. */
  async listInboundMedia(params: {
    types?: string;
    before?: string;
    limit?: number;
    channel?: 'whatsapp' | 'instagram';
  }): Promise<InboundMediaPage> {
    const response = await api.get<{ success: boolean; data: InboundMediaPage }>(
      '/admin/conversation-media',
      { params }
    );
    return response.data;
  }

  /** Read-only: what a sweep WOULD delete. Never touches S3 or the counter. */
  async previewInboundMediaSweep(
    olderThanDays: number,
    channel?: 'whatsapp' | 'instagram'
  ): Promise<{ count: number; bytes: number; capped: boolean }> {
    const response = await api.get<{
      success: boolean;
      data: { count: number; bytes: number; capped: boolean };
    }>('/admin/conversation-media/preview', { params: { olderThanDays, ...(channel ? { channel } : {}) } });
    return response.data;
  }

  /** Bulk delete past a retention window. Bounded server-side; `hasMore`
   *  signals that another call is needed to finish draining. */
  async sweepInboundMedia(
    olderThanDays: number,
    channel?: 'whatsapp' | 'instagram'
  ): Promise<{ deletedAttachments: number; reclaimedBytes: number; hasMore: boolean }> {
    const response = await api.post<{
      success: boolean;
      data: { deletedAttachments: number; reclaimedBytes: number; hasMore: boolean };
    }>('/admin/conversation-media/sweep', { olderThanDays, ...(channel ? { channel } : {}) });
    return response.data;
  }

  /** Delete a single attachment. Requires `activity.media` at full level. */
  async deleteInboundMedia(
    messageId: string,
    attachmentIndex: number,
    channel?: 'whatsapp' | 'instagram'
  ): Promise<void> {
    await api.delete(`/admin/conversation-media/${messageId}/${attachmentIndex}`, {
      params: channel ? { channel } : undefined,
    });
  }

  // ── Saved interactive messages (List / Button) ──────────────────────────
  // The tenant's reusable library. Referenced by id from the workflow canvas's
  // send nodes and (next) the AI's send_interactive_message tool, so these
  // records are authored once here rather than inline per node.

  async getInteractiveMessages(kind?: InteractiveMessageKind): Promise<InteractiveMessage[]> {
    const response = await api.get<{ success: boolean; data: { messages: InteractiveMessage[] } }>(
      '/whatsapp/interactive-messages',
      { params: kind ? { kind } : {} }
    );
    return response.data.messages;
  }

  async getInteractiveMessage(id: string): Promise<InteractiveMessage> {
    const response = await api.get<{ success: boolean; data: { message: InteractiveMessage } }>(
      `/whatsapp/interactive-messages/${id}`
    );
    return response.data.message;
  }

  async createInteractiveMessage(input: InteractiveMessageInput): Promise<InteractiveMessage> {
    const response = await api.post<{ success: boolean; data: { message: InteractiveMessage } }>(
      '/whatsapp/interactive-messages',
      input
    );
    return response.data.message;
  }

  async updateInteractiveMessage(
    id: string,
    input: InteractiveMessageInput
  ): Promise<InteractiveMessage> {
    const response = await api.put<{ success: boolean; data: { message: InteractiveMessage } }>(
      `/whatsapp/interactive-messages/${id}`,
      input
    );
    return response.data.message;
  }

  async deleteInteractiveMessage(id: string): Promise<void> {
    await api.delete(`/whatsapp/interactive-messages/${id}`);
  }

  /** ALL assets across every folder — used by the flow-builder file picker. */
  async getMediaAssets(type?: MediaAssetType): Promise<MediaAsset[]> {
    const response = await api.get<{ success: boolean; data: MediaAsset[] }>(
      '/admin/media-assets',
      { params: type ? { type } : {} }
    );
    return response.data;
  }

  /** Browse one folder: its sub-folders, its assets, and the breadcrumb path. */
  async browseMedia(folderId?: string | null): Promise<MediaBrowseResult> {
    const response = await api.get<{ success: boolean; data: MediaBrowseResult }>(
      '/admin/media-assets/browse',
      { params: folderId ? { folderId } : {} }
    );
    return response.data;
  }

  async uploadMediaAsset(
    file: File,
    opts?: { name?: string; description?: string; folderId?: string | null; type?: MediaAssetType }
  ): Promise<MediaAsset> {
    const formData = new FormData();
    formData.append('file', file);
    if (opts?.name) formData.append('name', opts.name);
    if (opts?.description) formData.append('description', opts.description);
    if (opts?.folderId) formData.append('folderId', opts.folderId);
    if (opts?.type) formData.append('type', opts.type);
    const response = await api.post<{ success: boolean; data: { asset: MediaAsset } }>(
      '/admin/media-assets',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.asset;
  }

  async updateMediaAsset(
    id: string,
    patch: { name?: string; description?: string; folderId?: string | null }
  ): Promise<MediaAsset> {
    const response = await api.patch<{ success: boolean; data: { asset: MediaAsset } }>(
      `/admin/media-assets/${id}`,
      patch
    );
    return response.data.asset;
  }

  async deleteMediaAsset(id: string): Promise<void> {
    await api.delete(`/admin/media-assets/${id}`);
  }

  async createMediaFolder(name: string, parentId?: string | null): Promise<MediaFolder> {
    const response = await api.post<{ success: boolean; data: { folder: MediaFolder } }>(
      '/admin/media-assets/folders',
      { name, ...(parentId ? { parentId } : {}) }
    );
    return response.data.folder;
  }

  async renameMediaFolder(id: string, name: string): Promise<MediaFolder> {
    const response = await api.patch<{ success: boolean; data: { folder: MediaFolder } }>(
      `/admin/media-assets/folders/${id}`,
      { name }
    );
    return response.data.folder;
  }

  async deleteMediaFolder(id: string): Promise<void> {
    await api.delete(`/admin/media-assets/folders/${id}`);
  }

  // Text Sources
  async getTextSources(
    params?: {
      page?: number;
      limit?: number;
      search?: string;
    },
    requestConfig?: RequestOptions
  ): Promise<{ data: TextSource[]; meta: any }> {
    const response = await api.get<{ success: boolean; data: TextSource[]; meta: any }>(
      '/admin/text',
      { params, ...requestConfig }
    );
    return { data: response.data, meta: response.meta };
  }

  async createTextSource(payload: { title: string; textContent: string }): Promise<TextSource> {
    const response = await api.post<{ success: boolean; data: { textSource: TextSource } }>(
      '/admin/text',
      payload
    );
    return response.data.textSource;
  }

  async updateTextSource(
    id: string,
    payload: { title: string; textContent: string }
  ): Promise<TextSource> {
    const response = await api.put<{ success: boolean; data: { textSource: TextSource } }>(
      `/admin/text/${id}`,
      payload
    );
    return response.data.textSource;
  }

  async deleteTextSource(id: string): Promise<void> {
    await api.delete(`/admin/text/${id}`);
  }

  // Agent File Attachments
  async getAgentFiles(agentId: string, requestConfig?: RequestOptions): Promise<FileSource[]> {
    const response = await api.get<{ success: boolean; data: { files: FileSource[] } }>(
      `/admin/agents/${agentId}/files`,
      requestConfig
    );
    return response.data.files;
  }

  async getFileAgents(fileId: string): Promise<Agent[]> {
    const response = await api.get<{ success: boolean; data: { agents: Agent[] } }>(
      `/admin/files/${fileId}/agents`
    );
    return normalizeAgentPayload(response.data.agents);
  }

  async attachFilesToAgents(fileIds: string[], agentIds: string[]): Promise<void> {
    await api.post('/admin/agents/files/attach', { fileIds, agentIds });
  }

  async detachFileFromAgent(
    agentId: string,
    fileId: string,
    deleteFile: boolean = false
  ): Promise<void> {
    await api.delete(`/admin/agents/${agentId}/files/${fileId}`, {
      params: { deleteFile: deleteFile.toString() },
    });
  }

  // Q&A Source Attachments (Agent <-> Q&A)
  async getAgentQASources(agentId: string, requestConfig?: RequestOptions): Promise<QASource[]> {
    const response = await api.get<{ success: boolean; data: { qas: QASource[] } }>(
      `/admin/agents/${agentId}/qa`,
      requestConfig
    );
    return response.data.qas;
  }

  async getQASourceAgents(qaId: string): Promise<Agent[]> {
    const response = await api.get<{ success: boolean; data: { agents: Agent[] } }>(
      `/admin/qa/${qaId}/agents`
    );
    return normalizeAgentPayload(response.data.agents);
  }

  async attachQASourcesToAgents(qaIds: string[], agentIds: string[]): Promise<void> {
    await api.post('/admin/agents/qa/attach', { qaIds, agentIds });
  }

  async detachQASourceFromAgent(agentId: string, qaId: string): Promise<void> {
    await api.delete(`/admin/agents/${agentId}/qa/${qaId}`);
  }

  // Text Source Attachments (Agent <-> Text)
  async getAgentTextSources(agentId: string, requestConfig?: RequestOptions): Promise<TextSource[]> {
    const response = await api.get<{ success: boolean; data: { texts: TextSource[] } }>(
      `/admin/agents/${agentId}/text`,
      requestConfig
    );
    return response.data.texts;
  }

  async getTextSourceAgents(textId: string): Promise<Agent[]> {
    const response = await api.get<{ success: boolean; data: { agents: Agent[] } }>(
      `/admin/text/${textId}/agents`
    );
    return normalizeAgentPayload(response.data.agents);
  }

  async attachTextSourcesToAgents(textIds: string[], agentIds: string[]): Promise<void> {
    await api.post('/admin/agents/text/attach', { textIds, agentIds });
  }

  async detachTextSourceFromAgent(agentId: string, textId: string): Promise<void> {
    await api.delete(`/admin/agents/${agentId}/text/${textId}`);
  }

  // Billing / Subscriptions (tenant admin)
  async getBillingActivePlan(): Promise<BillingActivePlanResponse> {
    const response = await api.get<{ success: boolean; data: BillingActivePlanResponse }>(
      '/admin/billing/active-plan'
    );
    return response.data;
  }

  async requestPlanUpgrade(planName: string): Promise<any> {
    const response = await api.post<{ success: boolean; data: any }>(
      '/admin/billing/upgrade-plan',
      { planName }
    );
    return response.data;
  }

  async requestPlanChange(planName: string): Promise<any> {
    const response = await api.post<{ success: boolean; data: any }>(
      '/admin/billing/upgrade-plan',
      { planName }
    );
    return response;
  }

  async getBillingUsageLimits(requestConfig?: RequestOptions): Promise<BillingUsageLimitsResponse> {
    const response = await api.get<{ success: boolean; data: BillingUsageLimitsResponse }>(
      '/admin/billing/usage-limits',
      requestConfig
    );
    return response.data;
  }

  /** All 8 quota meters (contacts/aiTokens/roles + the 5 core) for page-level usage chips. */
  async getUsageStatus(requestConfig?: RequestOptions): Promise<BillingUsageStatus> {
    const response = await api.get<{ success: boolean; data: BillingUsageStatus }>(
      '/admin/billing/usage-status',
      requestConfig
    );
    return response.data;
  }

  /** Which AI agent powers the public lead-capture chatbot (empty = scripted
   *  fallback), which external origins (if any) may embed/call it (empty =
   *  unrestricted, today's default), and the widget's display copy (empty
   *  string on any copy field = using the built-in default). */
  async getLeadFormSettings(requestConfig?: RequestOptions): Promise<LeadFormSettings> {
    const r = await api.get<{ success: boolean; data: LeadFormSettings }>(
      '/admin/lead-form-settings',
      requestConfig
    );
    return r.data;
  }

  /** Set (or clear) any subset of the lead-form settings. Every field is
   *  independently optional — omitting one leaves it untouched. Pass
   *  agentId: null to clear the agent, allowedOrigins: [] to clear the list,
   *  or '' on a copy field to revert it to the built-in default. */
  async updateLeadFormSettings(
    updates: Partial<Omit<LeadFormSettings, 'agentId'>> & { agentId?: string | null }
  ): Promise<LeadFormSettings> {
    const r = await api.patch<{ success: boolean; data: LeadFormSettings }>(
      '/admin/lead-form-settings',
      updates
    );
    return r.data;
  }

  /** Which AI agent is pinned to answer ALL inbound WhatsApp messages (null = dynamic keyword selection). */
  async getWhatsAppAgentSettings(
    requestConfig?: RequestOptions
  ): Promise<{ defaultAgentId: string | null }> {
    const r = await api.get<{ success: boolean; data: { defaultAgentId: string | null } }>(
      '/admin/whatsapp-agent-settings',
      requestConfig
    );
    return r.data;
  }

  /** Pin (or clear with null/'') the WhatsApp default AI agent. */
  async updateWhatsAppAgentSettings(
    agentId: string | null
  ): Promise<{ defaultAgentId: string | null }> {
    const r = await api.patch<{ success: boolean; data: { defaultAgentId: string | null } }>(
      '/admin/whatsapp-agent-settings',
      { agentId }
    );
    return r.data;
  }

  /** Instagram AI: whether auto-reply is ON for the workspace + the pinned agent
   *  (null = dynamic selection). Instagram defaults to human-handled, so aiEnabled
   *  is an explicit opt-in (unlike WhatsApp, which has no on/off gate). */
  async getInstagramAgentSettings(
    requestConfig?: RequestOptions
  ): Promise<{ aiEnabled: boolean; defaultAgentId: string | null }> {
    const r = await api.get<{
      success: boolean;
      data: { aiEnabled: boolean; defaultAgentId: string | null };
    }>('/admin/instagram-agent-settings', requestConfig);
    return r.data;
  }

  /** Turn Instagram AI on/off and/or pin its default agent. Only the fields
   *  passed are written (PATCH semantics). Pass agentId '' / null to clear the pin. */
  async updateInstagramAgentSettings(
    updates: { aiEnabled?: boolean; agentId?: string | null }
  ): Promise<{ aiEnabled: boolean; defaultAgentId: string | null }> {
    const r = await api.patch<{
      success: boolean;
      data: { aiEnabled: boolean; defaultAgentId: string | null };
    }>('/admin/instagram-agent-settings', updates);
    return r.data;
  }

  // ── Custom field-schema (typed lead/contact fields) ─────────────────────────
  // NOTE: this route is deliberately left ungated by requirePermission on the
  // backend (see admin.routes.ts's comment on GET /vertical + GET
  // /field-definitions) — it can never actually 403 with INSUFFICIENT_PERMISSIONS
  // today, but requestConfig is still threaded through defensively in case
  // that ever changes.
  async getFieldDefinitions(
    entity?: 'lead' | 'contact' | 'flow',
    requestConfig?: RequestOptions
  ): Promise<FieldDefinition[]> {
    const r = await api.get<{ success: boolean; data: FieldDefinition[] }>(
      '/admin/field-definitions',
      { params: entity ? { entity } : {}, ...requestConfig }
    );
    return r.data;
  }

  async createFieldDefinition(def: Partial<FieldDefinition>): Promise<FieldDefinition> {
    const r = await api.post<{ success: boolean; data: FieldDefinition }>(
      '/admin/field-definitions',
      def
    );
    return r.data;
  }

  async updateFieldDefinition(id: string, def: Partial<FieldDefinition>): Promise<FieldDefinition> {
    const r = await api.patch<{ success: boolean; data: FieldDefinition }>(
      `/admin/field-definitions/${id}`,
      def
    );
    return r.data;
  }

  async deleteFieldDefinition(id: string): Promise<void> {
    await api.delete(`/admin/field-definitions/${id}`);
  }

  // ── Flow-variable registry (entity='flow') ──────────────────────────────────
  // Managed under the AUTOMATION module, not verticals — flow variables belong to
  // the automation domain, so these go to /automation/flow-variables (gated by the
  // automation module), while lead/contact fields stay on /admin/field-definitions
  // (verticals). READS still use getFieldDefinitions('flow') (the open GET), so the
  // builder/API pickers list flow variables without any module dependency.
  async createFlowVariable(def: {
    key: string;
    label?: string;
    type?: FieldDefinition['type'];
    description?: string;
  }): Promise<FieldDefinition> {
    const r = await api.post<{ success: boolean; data: FieldDefinition }>(
      '/automation/flow-variables',
      def
    );
    return r.data;
  }

  async deleteFlowVariable(id: string): Promise<void> {
    await api.delete(`/automation/flow-variables/${id}`);
  }

  async getVerticalContext(): Promise<VerticalContext> {
    const r = await api.get<{ success: boolean; data: VerticalContext }>('/admin/vertical');
    return r.data;
  }

  async getScoringConfig(): Promise<LeadScoringConfig> {
    const r = await api.get<{ success: boolean; data: LeadScoringConfig }>(
      '/admin/leads/scoring-config'
    );
    return r.data;
  }

  async updateScoringConfig(
    config: Partial<LeadScoringConfig> & { acknowledgeDefaults?: boolean }
  ): Promise<LeadScoringConfig> {
    const r = await api.put<{ success: boolean; data: LeadScoringConfig }>(
      '/admin/leads/scoring-config',
      config
    );
    return r.data;
  }

  async resetScoringConfig(): Promise<LeadScoringConfig> {
    const r = await api.delete<{ success: boolean; data: LeadScoringConfig }>(
      '/admin/leads/scoring-config'
    );
    return r.data;
  }

  async getUsageDaily(params: { startDate?: string; endDate?: string }): Promise<UsageDailyResponse> {
    const response = await api.get<{ success: boolean; data: UsageDailyResponse }>(
      '/admin/billing/usage-daily',
      { params }
    );
    return response.data;
  }

  /** WhatsApp messages Meta bills us for, by category and by day. Both dates
   *  are optional — omitting them lets the server resolve the window itself
   *  (current billing period, else the last 30 days), which is what the
   *  Billing page asks for on first paint. */
  async getWhatsAppMessageUsage(params: {
    startDate?: string;
    endDate?: string;
  }): Promise<WhatsAppMessageUsage> {
    const response = await api.get<{ success: boolean; data: WhatsAppMessageUsage }>(
      '/admin/billing/whatsapp-messages',
      {
        params: {
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
        },
      }
    );
    return response.data;
  }

  /** Cross-check of the WhatsApp usage report against Meta's own pricing
   *  analytics for the same range. Slow-ish (one Meta call) and on demand only. */
  async verifyWhatsAppUsageWithMeta(params: {
    startDate: string;
    endDate: string;
  }): Promise<WhatsAppUsageVerification> {
    const response = await api.get<{ success: boolean; data: WhatsAppUsageVerification }>(
      '/admin/billing/whatsapp-messages/reconcile',
      { params }
    );
    return response.data;
  }

  async getUsagePeriods(params: { page?: number; limit?: number }): Promise<{
    data: UsagePeriodRecord[];
    meta: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
  }> {
    const response = await api.get<{
      success: boolean;
      data: UsagePeriodRecord[];
      meta: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
    }>('/admin/billing/usage-periods', { params });
    return { data: response.data, meta: response.meta };
  }

  async listBillingTransactions(params: { page?: number; limit?: number }): Promise<{
    data: BillingTransactionRecord[];
    meta: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
  }> {
    const response = await api.get<{
      success: boolean;
      data: BillingTransactionRecord[];
      meta: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
    }>('/admin/billing/transactions', { params });
    return { data: response.data, meta: response.meta };
  }

  async createCheckoutOrder(payload: CreateCheckoutOrderRequest): Promise<CreateCheckoutOrderResponse> {
    const response = await api.post<{ success: boolean; data: CreateCheckoutOrderResponse }>(
      '/admin/billing/checkout/create-order',
      payload
    );
    return response.data;
  }

  async confirmCheckoutOrder(gatewayOrderId: string): Promise<ConfirmCheckoutOrderResponse> {
    const response = await api.post<{ success: boolean; data: ConfirmCheckoutOrderResponse }>(
      '/admin/billing/checkout/confirm',
      { gatewayOrderId }
    );
    return response.data;
  }

  // Leads API
  async getLeads(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    source?: string;
    assignedTo?: string;
    priority?: string;
    /** Comma-separated tag names — a lead matches if it carries ANY of them. */
    tags?: string;
    /** Named relative window, resolved server-side against lastContactAt (falling
     *  back to createdAt). Omit, or send 'all', for no time filter. */
    dateRange?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }, requestConfig?: RequestOptions): Promise<{ data: any[]; pagination: any }> {
    const response = await api.get<{ success: boolean; data: any[]; pagination: any }>(
      '/admin/leads',
      { params, ...requestConfig }
    );
    return { data: response.data, pagination: response.pagination };
  }

  async getLeadById(leadId: string): Promise<any> {
    // lead.controller.ts::getLeadById responds with the lead object directly — `res.json(lead)`,
    // no {success,data} wrapper (verified against the controller; this endpoint is one of
    // several on this file that returns flat, unlike getTags/getCustomAttributeKeys below).
    const response = await api.get<any>(`/admin/leads/${leadId}`);
    return response;
  }

  /** GET /leads/:id/enquiries returns `{data, pagination}` directly (no success/data
   *  wrapper) — same shape as getLeads below, unlike getLeadById above. */
  async getLeadEnquiries(
    leadId: string,
    params: { page?: number; limit?: number } = {}
  ): Promise<{ data: any[]; pagination: any }> {
    const response = await api.get<{ data: any[]; pagination: any }>(
      `/admin/leads/${leadId}/enquiries`,
      { params }
    );
    return response;
  }

  async createLead(leadData: any): Promise<any> {
    // Flat response (`res.status(201).json(lead)`) — no {success,data} wrapper.
    const response = await api.post<any>('/admin/leads', leadData);
    return response;
  }

  async updateLead(leadId: string, leadData: any): Promise<any> {
    // Flat response (`res.json(lead)`) — no {success,data} wrapper.
    const response = await api.put<any>(`/admin/leads/${leadId}`, leadData);
    return response;
  }

  async deleteLead(leadId: string): Promise<void> {
    await api.delete(`/admin/leads/${leadId}`);
  }

  async getLeadStats(requestConfig?: RequestOptions): Promise<any> {
    // Flat response (`res.json(stats)`) — no {success,data} wrapper. Was previously
    // unwrapping `.data` here, which doesn't exist on the stats object, so every caller
    // silently got `undefined` (this is what made the Leads page stat cards always show 0
    // regardless of the real totals the backend was correctly computing).
    const response = await api.get<any>('/admin/leads/stats', requestConfig);
    return response;
  }

  /** `userId: null` unassigns. */
  async assignLead(leadId: string, userId: string | null): Promise<any> {
    // Flat response (`res.json(lead)`) — no {success,data} wrapper.
    const response = await api.post<any>(`/admin/leads/assign/${leadId}`, { agentId: userId });
    return response;
  }

  async scheduleLeadFollowUp(leadId: string): Promise<any> {
    // Flat response (`res.json({message, ...stats})`) — no {success,data} wrapper.
    const response = await api.post<any>(`/admin/leads/schedule-follow-up/${leadId}`);
    return response;
  }

  async bulkUpdateLeadStatus(leadIds: string[], status: string): Promise<{ updated: number }> {
    const response = await api.post<{ updated: number }>('/admin/leads/bulk-update-status', {
      leadIds,
      status,
    });
    return response;
  }

  async bulkDeleteLeads(leadIds: string[]): Promise<{ deleted: number }> {
    const response = await api.post<{ deleted: number }>('/admin/leads/bulk-delete', { leadIds });
    return response;
  }

  async mergeLeads(primaryLeadId: string, duplicateLeadIds: string[]): Promise<any> {
    const response = await api.post<any>('/admin/leads/merge', { primaryLeadId, duplicateLeadIds });
    return response;
  }

  /** Bulk-assign a set of conversations (by id) to one team member. */
  async bulkAssignContacts(
    conversationIds: string[],
    userId: string
  ): Promise<{ success: boolean; assigned: number; failed: Array<{ id: string; error: string }> }> {
    // Backend wraps payloads via sendSuccess → {success, data:{...}}; unwrap with a
    // flat-shape fallback so either envelope works.
    const response = await api.post<{
      success: boolean;
      assigned?: number;
      failed?: Array<{ id: string; error: string }>;
      data?: { assigned: number; failed: Array<{ id: string; error: string }> };
    }>('/admin/conversations/bulk-assign', { conversationIds, userId });
    const payload = response.data ?? response;
    return {
      success: response.success !== false,
      assigned: payload.assigned ?? 0,
      failed: payload.failed ?? [],
    };
  }

  /** Bulk-unassign a set of conversations (by id); optionally re-apply assignment rules. */
  async bulkUnassignContacts(
    conversationIds: string[],
    applyRules?: boolean
  ): Promise<{ success: boolean; unassigned: number; rulesApplied: number; failed: Array<{ id: string; error: string }> }> {
    // Backend wraps payloads via sendSuccess → {success, data:{...}}; unwrap with a
    // flat-shape fallback so either envelope works.
    const response = await api.post<{
      success: boolean;
      unassigned?: number;
      rulesApplied?: number;
      failed?: Array<{ id: string; error: string }>;
      data?: { unassigned: number; rulesApplied: number; failed: Array<{ id: string; error: string }> };
    }>('/admin/conversations/bulk-unassign', { conversationIds, applyRules });
    const payload = response.data ?? response;
    return {
      success: response.success !== false,
      unassigned: payload.unassigned ?? 0,
      rulesApplied: payload.rulesApplied ?? 0,
      failed: payload.failed ?? [],
    };
  }

  /** Bulk-assign a set of leads (by id) to one team member. */
  async bulkAssignLeads(
    leadIds: string[],
    userId: string
  ): Promise<{ success: boolean; assigned: number; failed: Array<{ id: string; error: string }> }> {
    // Backend returns flat {success, assigned, failed} — no data envelope.
    const response = await api.post<{
      success: boolean;
      assigned?: number;
      failed?: Array<{ id: string; error: string }>;
      data?: { assigned: number; failed: Array<{ id: string; error: string }> };
    }>('/admin/leads/bulk-assign', { leadIds, userId });
    const payload = response.data ?? response;
    return {
      success: response.success !== false,
      assigned: payload.assigned ?? 0,
      failed: payload.failed ?? [],
    };
  }

  /** Fetch all tag-based assignment rules for this tenant. */
  async getContactAssignmentRules(): Promise<AssignmentRule[]> {
    const response = await api.get<{
      success: boolean;
      rules?: AssignmentRule[];
      data?: { rules?: AssignmentRule[] };
    }>('/admin/conversations/assignment-rules');
    const rules = response.rules ?? response.data?.rules;
    return Array.isArray(rules) ? rules : [];
  }

  /** Overwrite the full assignment rules list (whole-array PUT). */
  async saveContactAssignmentRules(rules: AssignmentRule[]): Promise<AssignmentRule[]> {
    const response = await api.put<{
      success: boolean;
      rules?: AssignmentRule[];
      data?: { rules?: AssignmentRule[] };
    }>('/admin/conversations/assignment-rules', { rules });
    const saved = response.rules ?? response.data?.rules;
    return Array.isArray(saved) ? saved : rules;
  }

  /** Apply all tag-based rules to currently-unassigned conversations and return a summary. */
  async applyContactAssignmentRules(): Promise<{
    success: boolean;
    scanned: number;
    assigned: number;
    perRule: Array<{ ruleId: string; matched: number; assigned: number }>;
  }> {
    const response = await api.post<{
      success: boolean;
      scanned?: number;
      assigned?: number;
      perRule?: Array<{ ruleId: string; matched: number; assigned: number }>;
      data?: {
        scanned: number;
        assigned: number;
        perRule: Array<{ ruleId: string; matched: number; assigned: number }>;
      };
    }>('/admin/conversations/assignment-rules/apply');
    const payload = response.data ?? response;
    return {
      success: response.success !== false,
      scanned: payload.scanned ?? 0,
      assigned: payload.assigned ?? 0,
      perRule: payload.perRule ?? [],
    };
  }

  async importLeadsFromExcel(
    rows: Record<string, any>[],
    fieldMapping: Record<string, string>,
    options?: { fileName?: string; tags?: string[] }
  ): Promise<{
    imported: number;
    updated: number;
    skipped: number;
    errors: Array<{ row: number; error: string }>;
  }> {
    // lead.controller.ts's importFromExcel responds with the raw result object (no
    // {success,data} envelope — unlike contacts' sendSuccess-wrapped response), so this
    // is the body itself, not response.data.
    const response = await api.post<{
      imported: number;
      updated: number;
      skipped: number;
      errors: Array<{ row: number; error: string }>;
    }>('/admin/leads/import/excel', {
      rows,
      fieldMapping,
      options,
    });
    return response;
  }

  async downloadLeadImportTemplate(): Promise<Blob> {
    const response = await api.get('/admin/leads/import/template', {
      responseType: 'blob',
    });
    return response as any;
  }

  async getLeadTags(): Promise<string[]> {
    // Endpoint returns the flat {success, tags} contract; tolerate the older
    // {data} envelope too and never resolve undefined — these feed Autocomplete
    // options, and undefined options crash the whole page render.
    const response = await api.get<{ success: boolean; tags?: string[]; data?: string[] }>(
      '/admin/leads/tags'
    );
    const tags = response?.tags ?? response?.data;
    return Array.isArray(tags) ? tags : [];
  }

  async getLeadAttributeKeys(requestConfig?: RequestOptions): Promise<string[]> {
    const response = await api.get<{ success: boolean; data: string[] }>(
      '/admin/leads/attribute-keys',
      requestConfig
    );
    return response.data;
  }

  // Lead Scoring API
  async calculateLeadScore(leadId: string): Promise<{
    success: boolean;
    leadId: string;
    score: number;
    priority: string;
    appliedRules: string[];
  }> {
    const response = await api.post<{
      success: boolean;
      leadId: string;
      score: number;
      priority: string;
      appliedRules: string[];
    }>(`/admin/leads/${leadId}/score`);
    return response;
  }

  async scoreAllLeads(): Promise<{
    success: boolean;
    message: string;
    scored: number;
    failed: number;
    changed?: number;
  }> {
    const response = await api.post<{
      success: boolean;
      message: string;
      scored: number;
      failed: number;
      changed?: number;
    }>('/admin/leads/score-all');
    return response;
  }

  async getLeadsByPriority(
    priority: 'low' | 'medium' | 'high' | 'hot',
    limit?: number
  ): Promise<{
    success: boolean;
    priority: string;
    count: number;
    leads: any[];
  }> {
    const response = await api.get<{
      success: boolean;
      priority: string;
      count: number;
      leads: any[];
    }>(`/admin/leads/by-priority/${priority}`, {
      params: { limit },
    });
    return response;
  }

  async getLeadScoringStats(): Promise<{
    success: boolean;
    totalLeads: number;
    avgScore: number;
    byPriority: Record<string, number>;
  }> {
    const response = await api.get<{
      success: boolean;
      totalLeads: number;
      avgScore: number;
      byPriority: Record<string, number>;
    }>('/admin/leads/scoring/stats');
    return response;
  }

  async getLeadScoreBreakdown(leadId: string): Promise<{
    score: number;
    priority: string;
    breakdown: ScoreBreakdownEntry[];
    calculatedAt?: string;
  }> {
    const response = await api.get<{
      success: boolean;
      data: { score: number; priority: string; breakdown: ScoreBreakdownEntry[]; calculatedAt?: string };
    }>(`/admin/leads/${leadId}/score-breakdown`);
    return response.data;
  }

  // CRM Webhook Integrations
  async getWebhookInfo(): Promise<{
    meta: {
      url: string;
      status: 'active' | 'inactive' | 'error';
      lastReceived?: string;
      totalLeads?: number;
    };
  }> {
    const storedTenant = localStorage.getItem('admin_tenant');
    const storedUser = localStorage.getItem('admin_user');
    let tenantId = '';
    try {
      const tenant = storedTenant ? JSON.parse(storedTenant) : null;
      const user = storedUser ? JSON.parse(storedUser) : null;
      tenantId = tenant?.id || user?.tenantId || '';
    } catch {
      tenantId = '';
    }

    if (!tenantId) {
      throw new Error('Tenant ID not found in session');
    }
    const data = await api.get<{
      success: boolean;
      webhooks: {
        meta: {
          url: string;
          status: 'active' | 'inactive' | 'error';
          lastReceived?: string;
          totalLeads?: number;
        };
      };
    }>(`/webhooks/crm/info/${tenantId}`);
    return data.webhooks;
  }

  // ─── HubSpot Connector (src/modules/connectors) ────────────────────────────
  // OAuth-based — a "Connect" button, never a pasted secret. See
  // pages/admin/Integrations.tsx for the connection-card UI these back.

  async getHubSpotAuthorizeUrl(): Promise<string> {
    const data = await api.get<{ success: boolean; data: { url: string } }>(
      '/admin/connectors/hubspot/authorize-url'
    );
    return data.data.url;
  }

  async getHubSpotConnectorStatus(): Promise<HubSpotConnectorStatus> {
    const data = await api.get<{ success: boolean; data: HubSpotConnectorStatus }>(
      '/admin/connectors/hubspot/status'
    );
    return data.data;
  }

  async disconnectHubSpotConnector(): Promise<void> {
    await api.post('/admin/connectors/hubspot/disconnect');
  }

  async triggerHubSpotSync(): Promise<void> {
    await api.post('/admin/connectors/hubspot/sync');
  }

  // ─── Google Sheets Connector (src/modules/connectors) ──────────────────────
  // READ-ONLY. One OAuth grant per tenant (a grant belongs to a Google
  // account), with many sheet "sources" beneath it — one per spreadsheet TAB.
  // See modules/connectors/models/SheetSource.model.ts.

  async getGoogleSheetsAuthorizeUrl(): Promise<{
    url: string;
    clientId: string | null;
    pickerApiKey: string | null;
  }> {
    const data = await api.get<{
      success: boolean;
      data: { url: string; clientId: string | null; pickerApiKey: string | null };
    }>('/admin/connectors/google-sheets/authorize-url');
    return data.data;
  }

  async getGoogleSheetsStatus(): Promise<GoogleSheetsConnectorStatus> {
    const data = await api.get<{ success: boolean; data: GoogleSheetsConnectorStatus }>(
      '/admin/connectors/google-sheets/status'
    );
    return data.data;
  }

  async disconnectGoogleSheets(): Promise<void> {
    await api.post('/admin/connectors/google-sheets/disconnect');
  }

  async listGoogleSheetTabs(spreadsheetId: string): Promise<GoogleSheetTab[]> {
    const data = await api.get<{ success: boolean; data: { tabs: GoogleSheetTab[] } }>(
      '/admin/connectors/google-sheets/tabs',
      { params: { spreadsheetId } }
    );
    return data.data.tabs;
  }

  async previewGoogleSheetTab(params: {
    spreadsheetId: string;
    sheetTabId: number;
    headerRow?: number;
  }): Promise<GoogleSheetPreview> {
    const data = await api.get<{ success: boolean; data: GoogleSheetPreview }>(
      '/admin/connectors/google-sheets/preview',
      { params }
    );
    return data.data;
  }

  async createGoogleSheetSource(payload: {
    spreadsheetId: string;
    spreadsheetName: string;
    sheetTabId: number;
    sheetTabName: string;
    headerRow: number;
    lastColumn: string;
    mapping: Record<string, string>;
    importTags?: string[];
    mode: GoogleSheetSyncMode;
  }): Promise<GoogleSheetSource> {
    const data = await api.post<{ success: boolean; data: GoogleSheetSource }>(
      '/admin/connectors/google-sheets/sources',
      payload
    );
    return data.data;
  }

  async updateGoogleSheetSource(
    sourceId: string,
    payload: Partial<{
      mapping: Record<string, string>;
      importTags: string[];
      mode: GoogleSheetSyncMode;
      status: 'active' | 'paused';
      headerRow: number;
    }>
  ): Promise<GoogleSheetSource> {
    const data = await api.patch<{ success: boolean; data: GoogleSheetSource }>(
      `/admin/connectors/google-sheets/sources/${sourceId}`,
      payload
    );
    return data.data;
  }

  async deleteGoogleSheetSource(sourceId: string): Promise<void> {
    await api.delete(`/admin/connectors/google-sheets/sources/${sourceId}`);
  }

  /** `full: true` resets the cursor and re-reads the whole tab. Safe at any
   *  time — leads merge on phone-or-email, so a re-read updates in place. */
  async triggerGoogleSheetSync(sourceId: string, full = false): Promise<void> {
    await api.post(`/admin/connectors/google-sheets/sources/${sourceId}/sync`, { full });
  }

  // ─── Automation / Workflow API ──────────────────────────────────────────────

  async getWorkflows(
    params?: {
      isActive?: boolean;
      agentId?: string;
      triggerType?: string;
    },
    requestConfig?: RequestOptions
  ): Promise<any[]> {
    const response = await api.get<any[]>('/automation/workflows', { params, ...requestConfig });
    return Array.isArray(response) ? response : ((response as any).data ?? []);
  }

  /** Static starter templates (both engines) the builder can prefill so an admin
   *  can learn automation by example. Ungated read — see automation.routes.ts. */
  async getWorkflowTemplates(): Promise<any[]> {
    const response = await api.get<{ templates: any[] }>('/automation/workflows/templates');
    const body = (response as any)?.data ?? response;
    return Array.isArray(body?.templates) ? body.templates : [];
  }

  async getWorkflowById(id: string): Promise<any> {
    const response = await api.get<any>(`/automation/workflows/${id}`);
    return (response as any).data ?? response;
  }

  async createWorkflow(data: any): Promise<any> {
    const response = await api.post<any>('/automation/workflows', data);
    return (response as any).data ?? response;
  }

  async updateWorkflow(id: string, data: any): Promise<any> {
    const response = await api.patch<any>(`/automation/workflows/${id}`, data);
    return (response as any).data ?? response;
  }

  async deleteWorkflow(id: string): Promise<void> {
    await api.delete(`/automation/workflows/${id}`);
  }

  /** Set routing precedence: `orderedIds` top-to-bottom → each row's priority
   *  becomes its index. Used by the tenant Routing view's drag-to-reorder. */
  async reorderWorkflows(orderedIds: string[]): Promise<void> {
    await api.post('/automation/workflows/reorder', { orderedIds });
  }

  /**
   * Dry-run a customer message against the tenant's live conversation flows.
   * It never executes nodes, so it cannot alter a real conversation or contact.
   * `draft` lets an author test unsaved canvas edits; `session` + `liveApi` make it a
   * multi-turn test where "Get User Input" waits for an answer and "Call API" can
   * run for real.
   */
  async simulateWorkflowMessage(payload: {
    message: string;
    /** Simulate a button/list-item tap instead of typed text — checked before
     *  keyword matching, mirroring the live evaluator's precedence. */
    buttonReplyId?: string;
    workflowId?: string;
    draft?: { nodes: any[]; edges: any[] };
    /** The `session` returned by the previous turn while the flow waits on an answer. */
    session?: any;
    /** Really call "Call API" steps (opt-in). Off = they are reported as not called. */
    liveApi?: boolean;
  }): Promise<any> {
    const response = await api.post<any>('/automation/workflows/simulate', payload);
    return (response as any).data ?? response;
  }

  async activateWorkflow(id: string): Promise<any> {
    const response = await api.post<any>(`/automation/workflows/${id}/activate`);
    return (response as any).data ?? response;
  }

  async deactivateWorkflow(id: string): Promise<any> {
    const response = await api.post<any>(`/automation/workflows/${id}/deactivate`);
    return (response as any).data ?? response;
  }

  async triggerWorkflow(id: string, leadId: string, dryRun = false): Promise<any> {
    const response = await api.post<any>(`/automation/workflows/${id}/trigger`, { leadId, dryRun });
    return (response as any).data ?? response;
  }

  async getWorkflowExecutions(params?: {
    workflowId?: string;
    status?: string;
    leadId?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await api.get<any[]>('/automation/executions', { params });
    return Array.isArray(response) ? response : ((response as any).data ?? []);
  }

  /** Paginated variant of getWorkflowExecutions. Passing `page` (1-indexed) opts
   *  the backend into its `{ data, meta }` envelope; the array-returning method
   *  above is left untouched for callers that just want the newest N runs. */
  async getWorkflowExecutionsPage(params: {
    workflowId?: string;
    status?: string;
    leadId?: string;
    page: number;
    limit: number;
  }): Promise<{ data: any[]; meta: { page: number; limit: number; total: number } }> {
    const response = await api.get<any>('/automation/executions', { params });
    const data = Array.isArray(response) ? response : ((response as any)?.data ?? []);
    const meta = (response as any)?.meta ?? {
      page: params.page,
      limit: params.limit,
      total: data.length,
    };
    return { data, meta };
  }

  async getWorkflowExecutionById(executionId: string): Promise<any> {
    const response = await api.get<any>(`/automation/executions/${executionId}`);
    return (response as any).data ?? response;
  }

  async getLeadExecutions(leadId: string): Promise<any[]> {
    const response = await api.get<any[]>(`/automation/leads/${leadId}/executions`);
    return Array.isArray(response) ? response : ((response as any).data ?? []);
  }

  /** Conversation-flow (message.received) run history — a separate, smaller
   *  concept from getWorkflowExecutions above (the async CRM engine's runs). */
  async getConversationFlowRuns(workflowId: string, limit: number = 50): Promise<any[]> {
    const response = await api.get<any[]>(`/automation/workflows/${workflowId}/runs`, {
      params: { limit },
    });
    return Array.isArray(response) ? response : ((response as any).data ?? []);
  }

  /** Paginated variant of getConversationFlowRuns — same `page`/`{ data, meta }`
   *  opt-in as getWorkflowExecutionsPage. */
  async getConversationFlowRunsPage(
    workflowId: string,
    page: number,
    limit: number
  ): Promise<{ data: any[]; meta: { page: number; limit: number; total: number } }> {
    const response = await api.get<any>(`/automation/workflows/${workflowId}/runs`, {
      params: { page, limit },
    });
    const data = Array.isArray(response) ? response : ((response as any)?.data ?? []);
    const meta = (response as any)?.meta ?? { page, limit, total: data.length };
    return { data, meta };
  }

  /** Real campaign delivery outcome(s) for a workflow execution's send_campaign node(s). */
  async getExecutionCampaignActivity(executionId: string): Promise<any[]> {
    const response = await api.get<any[]>(
      `/automation/executions/${executionId}/campaign-activity`
    );
    return Array.isArray(response) ? response : ((response as any).data ?? []);
  }

  async getCampaigns(params?: { limit?: number }, requestConfig?: RequestOptions): Promise<any[]> {
    const response = await api.get<any>('/admin/campaigns', {
      params: { limit: 100, ...params },
      ...requestConfig,
    });
    if (Array.isArray(response)) return response;
    if (Array.isArray((response as any).data)) return (response as any).data;
    return [];
  }

  async getCampaignById(id: string, requestConfig?: RequestOptions): Promise<any> {
    const response = await api.get<{ success: boolean; data: { campaign: any } }>(
      `/admin/campaigns/${id}`,
      requestConfig
    );
    return response.data.campaign;
  }

  async createCampaign(payload: any): Promise<any> {
    const response = await api.post<{ success: boolean; data: { campaign: any } }>(
      '/admin/campaigns',
      payload
    );
    return response.data.campaign;
  }

  async updateCampaign(id: string, payload: any): Promise<any> {
    const response = await api.put<{ success: boolean; data: { campaign: any } }>(
      `/admin/campaigns/${id}`,
      payload
    );
    return response.data.campaign;
  }

  async getSegmentOptions(requestConfig?: RequestOptions): Promise<{
    tags: string[];
    countryCodes: string[];
    statuses: string[];
    agents: Array<{ _id: string; name: string }>;
    customAttributeKeys: string[];
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        tags: string[];
        countryCodes: string[];
        statuses: string[];
        agents: Array<{ _id: string; name: string }>;
        customAttributeKeys: string[];
      };
    }>('/admin/campaigns/segment-options', requestConfig);
    return response.data;
  }

  // Total contact count for the tenant, unfiltered — powers the "Segment
  // breakdown" sidebar in CampaignForm. Deliberately NOT getConversations()
  // (that endpoint fetches at most 5000 documents before computing its
  // meta.total from the fetched array's length, so it silently plateaus at
  // 5000 for larger tenants) — this dedicated endpoint runs a direct
  // countDocuments instead (see campaign.service.ts's getTotalContactCount).
  async getTotalContactCount(requestConfig?: RequestOptions): Promise<number> {
    const response = await api.get<{ success: boolean; data: { count: number } }>(
      '/admin/campaigns/contact-count',
      requestConfig
    );
    return response.data.count ?? 0;
  }

  async estimateReach(
    targeting: any,
    limits: { maxRecipients?: number } | undefined,
    campaignId: string | undefined,
    requestConfig?: RequestOptions
  ): Promise<number | null> {
    const response = await api.post<{ success: boolean; data: { reach: number } }>(
      '/admin/campaigns/estimate-reach',
      { targeting, limits, campaignId },
      requestConfig
    );
    return response.data?.reach ?? null;
  }

  // System Alerts
  async getAlerts(params?: {
    resolved?: boolean;
    type?: AlertType;
    category?: AlertCategory;
    code?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: SystemAlert[]; meta: any }> {
    const response = await api.get<{ success: boolean; data: SystemAlert[]; meta: any }>(
      '/admin/alerts',
      { params }
    );
    return { data: response.data, meta: response.meta };
  }

  async getAlertSummary(requestConfig?: RequestOptions): Promise<AlertSummary> {
    const response = await api.get<{ success: boolean; data: AlertSummary }>(
      '/admin/alerts/summary',
      requestConfig
    );
    return response.data;
  }

  async getActivityBadges(requestConfig?: RequestOptions): Promise<ActivityBadges> {
    const response = await api.get<{ success: boolean; data: ActivityBadges }>(
      '/admin/activity-badges',
      requestConfig
    );
    return response.data;
  }

  async markActivitySeen(section: ActivitySection): Promise<void> {
    await api.post('/admin/activity-badges/seen', { section });
  }

  async acknowledgeAlert(id: string): Promise<void> {
    await api.put(`/admin/alerts/${id}/acknowledge`);
  }

  async resolveAlert(id: string): Promise<void> {
    await api.put(`/admin/alerts/${id}/resolve`);
  }

  // ─── External API Gateway (integrated agents) ──────────────────────────────

  async getExternalApiClients(): Promise<ExternalApiClient[]> {
    const response = await api.get<{ success: boolean; data: ExternalApiClient[] }>(
      '/admin/external-clients'
    );
    return response.data;
  }

  async createExternalApiClient(
    input: CreateExternalApiClientInput
  ): Promise<ExternalApiClientSecretReveal> {
    const response = await api.post<{ success: boolean; data: ExternalApiClientSecretReveal }>(
      '/admin/external-clients',
      input
    );
    return response.data;
  }

  async rotateExternalApiClient(
    id: string
  ): Promise<{ client: ExternalApiClient; apiKey: string }> {
    const response = await api.post<{
      success: boolean;
      data: { client: ExternalApiClient; apiKey: string };
    }>(`/admin/external-clients/${id}/rotate`);
    return response.data;
  }

  async revokeExternalApiClient(id: string): Promise<ExternalApiClient> {
    const response = await api.post<{ success: boolean; data: ExternalApiClient }>(
      `/admin/external-clients/${id}/revoke`
    );
    return response.data;
  }

  async updateExternalApiClientCallback(
    id: string,
    callbackUrl: string | null
  ): Promise<{ client: ExternalApiClient; callbackSigningSecret?: string }> {
    const response = await api.patch<{
      success: boolean;
      data: { client: ExternalApiClient; callbackSigningSecret?: string };
    }>(`/admin/external-clients/${id}/callback`, { callbackUrl });
    return response.data;
  }

  /** Issues a brand-new signing secret for a client that already has a callback URL —
   *  the only way to recover from a secret that can no longer be verified/decrypted, or
   *  to rotate it as routine hygiene. No grace period: unlike the API key, we're the
   *  signer here, not the verifier, so the old secret simply stops being used the moment
   *  this saves — the admin must update their receiver's verification key to match. */
  async regenerateExternalApiClientCallbackSecret(
    id: string
  ): Promise<{ client: ExternalApiClient; callbackSigningSecret: string }> {
    const response = await api.post<{
      success: boolean;
      data: { client: ExternalApiClient; callbackSigningSecret: string };
    }>(`/admin/external-clients/${id}/callback/regenerate-secret`);
    return response.data;
  }

  async updateExternalApiClientScopes(
    id: string,
    scopes: ExternalApiScope[]
  ): Promise<ExternalApiClient> {
    const response = await api.patch<{ success: boolean; data: ExternalApiClient }>(
      `/admin/external-clients/${id}/scopes`,
      { scopes }
    );
    return response.data;
  }

  /** Replaces the agent's record-change subscriptions wholesale. Pass [] to unsubscribe from
   *  everything. Rejected with a 400 if the agent has no callback URL set. */
  async updateExternalApiClientRecordSubscriptions(
    id: string,
    recordSubscriptions: RecordSubscription[],
    suppressSelfCausedEvents?: boolean
  ): Promise<ExternalApiClient> {
    const response = await api.patch<{ success: boolean; data: ExternalApiClient }>(
      `/admin/external-clients/${id}/record-subscriptions`,
      { recordSubscriptions, suppressSelfCausedEvents: Boolean(suppressSelfCausedEvents) }
    );
    return response.data;
  }
}

export default new AdminApiService();
